import _wsShared from '@symbo-ls/workspace-shared'
import { shellGetAiService } from './shellSharedDelegates.js'
// shared's OWN registered functions (peopleUtils, dateUtils, avatarUrl,
// canvasUrl, slugify, profilePath, applyLoader, closeModalByStateKey, …)
// are NOT re-registered here. `shared` is a declared library of the shell
// (sharedLibraries.js), so its functions bag is already merged into every
// shell context and `el.call('findPerson')` reaches shared's own function
// with `this` = the element. A shell-side delegate under the SAME key was a
// second definition: in a catalog page that merges both libraries the merge
// order picked one, and when the shell's delegate won, its
// `_wsShared.functions.<name>` lookup could resolve back to itself.
//
// HOUSE PATTERN — every deferred-call wrapper here MUST be a `function`
// expression forwarding via `.apply(this, args)`, NEVER an arrow. el.call
// invokes registered functions with `this` = the element
// (smbls element/src/methods.js `method.call(this, ...args)`), and several
// shared impls fall back to `this.state.root` when called with no args
// (peopleByEmail, findPerson, isSelf, avatarUrlFor, activeMembers) or use
// `this.getRoot()` outright (applyLoader). An arrow wrapper silently
// rebinds `this` to `_wsShared.functions`, so the zero-arg fallback
// resolved no root and e.g. `el.call('peopleByEmail')` returned {} —
// AvatarBundle rendered zero avatars against a populated root.people
// (found live 2026-08-13). Same convention as `closeModal` (./closeModal.js).
const shellCreateIframeApp = function (...args) {
  return _wsShared.functions.createIframeApp.apply(this, args)
}
const shellInjectSprites = function (...args) {
  return _wsShared.functions.injectSprites.apply(this, args)
}
// `deepClone` sourced directly from @symbo.ls/utils, NOT the 'smbls' umbrella
// (see functions/extensionDesignSystem.js for the full why — same fix).
import { deepClone } from '@symbo.ls/utils'
import { activeWorkspaceId } from './scopedClient.js'
import {
  buildExtensionDataBridge,
  auditExtensionSource,
  declaredEntitiesFromSpec
} from './extensionDataBridge.js'
import { buildRootStateSeed } from './extensionDataSeed.js'
import {
  GENERATED_APP_BLOCKED,
  generatedAppAllowlist,
  parseGeneratedAppSource,
  auditGeneratedAppSource,
  scopeGeneratedAppFunctions
} from './generatedAppContainment.js'
import {
  generatedAppSandboxOn,
  sandboxAppDef,
  buildSandboxedAppDef,
  sandboxWrapperEntities,
  sandboxComponentRefusals,
  pageStateSeed as _pageStateSeed
} from './generatedAppSandbox.js'
import { extensionDesignSystemSource } from './extensionDesignSystem.js'
import { normalizePages, buildNav, buildAppShell } from './multiPageApp.js'
import { setLastRenderedExtension } from './extensionSaveShare.js'
import { generatedNotifyHref } from './generatedNotifyHref.js'
// The CRM visitor line under a built app's sign-up form (POB, Nika 10-31 r124 A).
import { crmLeadsVisitorLine } from './crmLeadsVisitorLine.js'
// Slugify is imported (not re-exported via `export { x } from`) because
// Parcel's named-re-export pass on this branch silently drops the
// module-map dep entry for re-export-only paths, leading to a runtime
// `Cannot find module '../../shared/functions/slugify.js'` from the
// bundled workspace-project/functions/index.js. Routing through an
// explicit `import … ; export { … }` forces Parcel to register the
// dependency in the per-module deps map like every other import here.
import {
  homeContextAction as _homeContextAction,
  focusHomeContextMenu as _focusHomeContextMenu,
  homeContextMenuAllowed as _homeContextMenuAllowed,
  closeContextMenuOnEscape as _closeContextMenuOnEscape,
  closeContextMenuOnRouteChange as _closeContextMenuOnRouteChange,
  homeWidgetAiTarget as _homeWidgetAiTarget,
  homeWidgetChangeWithAi as _homeWidgetChangeWithAi
} from './homeContextMenu.js'
import { focusContextMenu as _focusContextMenu } from './contextMenuFocus.js'

// Home dashboard right-click menus dispatch through this (the empty-area
// [Refresh, Customize] menu + the per-widget menu). Rows/coords are built at the
// open site in pages/main.js; this delegates data refetch + widget-hide back to
// the page via root._homeOps. See functions/homeContextMenu.js.
export const homeContextAction = _homeContextAction
// INTERACT-1: focus-into-menu fix so Escape/arrow-keys reach the shared
// ContextDropdown's own Menu.onKeydown. The panel is the SHELL-WIDE
// `root.contextDropdown` singleton, so the canonical name is neutral —
// `el.call('focusContextMenu')` from any surface, Home or a module page. See
// functions/contextMenuFocus.js.
export const focusContextMenu = _focusContextMenu
// The original Home-scoped name, kept as a thin alias: modules that already
// shipped `el.call('focusHomeContextMenu')` (VaultOS 1.38.1) and Home's own
// three openers keep resolving. See functions/homeContextMenu.js.
export const focusHomeContextMenu = _focusHomeContextMenu
// D2 §5 (P1): the home board must not answer a right-click while a PageSlot
// page is open over it, and the menu must close on Escape and on a route
// change regardless of where focus is. See functions/homeContextMenu.js.
export const homeContextMenuAllowed = _homeContextMenuAllowed
export const closeContextMenuOnEscape = _closeContextMenuOnEscape
export const closeContextMenuOnRouteChange = _closeContextMenuOnRouteChange
// "Change with AI…" on a home card (HOME-WIDGET-CHANGE-WITH-AI-1): which cards
// get the row, and the action that attaches the card to the composer. See
// functions/homeContextMenu.js.
export const homeWidgetAiTarget = _homeWidgetAiTarget
export const homeWidgetChangeWithAi = _homeWidgetChangeWithAi

export { ensureChatContext, openMessenger } from './chatFold.js'
// Mail-side twin of ensureChatContext — folds the deferred mail context and
// fills the shell-root MailOverlaySlot before the header envelope opens the
// docked MailOverlay. See ./mailFold.js.
export { ensureMailContext } from './mailFold.js'
// /files opens a .md in the notes editor, from a route that never folds
// documents — see the header of documentsFold.js.
export { ensureDocumentsContext } from './documentsFold.js'
// Meet-side twin of ensureChatContext — folds the deferred meet context for
// surfaces that need a meet component key while meet's chunk is unloaded
// (today: chat's channel-header group call, whose modal body extends
// MeetVideoRoom). See ./meetFold.js.
export { ensureMeetContext } from './meetFold.js'
// The App Store's remove dialog for /add-app's own Remove — folds the deferred
// marketplace context and fills the shell-root MarketplaceUninstallSlot. See
// ./marketplaceUninstallDialog.js.
export { ensureMarketplaceUninstallDialog } from './marketplaceUninstallDialog.js'
// Was defined in permissions.js but never exported through this barrel, so
// every caller (chat/tickets/files/documents pages' onCreate external-only
// gate) el.call'd an unregistered name — a safe-returning undefined that
// silently SKIPPED the redirect for external-only users (caught by the
// chat-fold verification sweep, 2026-08-01).
export { redirectIfExternalOnly } from './permissions.js'

// changeGlobalTheme is shared's registration (shared/functions/changeGlobalTheme.js);
// MeMenu, home look and the assistant's set_theme reach it via el.call.

// (colorFromEmail + its 7-hue PALETTE retired 2026-08-08 — monogram plates
// are house identity plates now; 5 of the 7 hues failed AA under white ink.)

// `initials` lives in ./roleHelpers.js beside the row mapper that uses it —
// ONE definition for both person mappers; re-exported here by name (the
// shell's loadPrivateData imports it from this barrel).
export { initials } from './roleHelpers.js'

// Role helpers moved to ./roles.js (2026-08-12) so AdminChrome and other
// components reachable from the workspace-shared components barrel can
// import them without pulling this whole barrel into the shared package's
// init cycle (that cycle crashed the shell boot — see roles.js header).
// Re-exported here so `el.call` registration and barrel imports are
// unchanged.
export * from './roles.js'

// formatTime / formatMonthShort / formatShortDate: ONE self-contained source
// for the shell and the sandbox frame (sandboxFormatters.js).
export { formatTime, formatMonthShort, formatShortDate } from './sandboxFormatters.js'

// Re-export all shared-gov functions so they're available via el.call()
// Policies + Claims (GreenIG insurance book) — mocked data + formatters/filters
// for the /policies + /claims pages, the s1m0ne policies card, and the shared
// record-detail modal opened on row click.
// NOTE — the GreenIG insurance data + record-detail modal functions live in
// the PUBLISHED v2 module project (silverbreeze-labs/greenig-app) and inject
// into context.functions only when that workspace activates
// (functions/workspaceProjects.js). The legacy bundled copies were deleted
// with the WS6 migration (2026-07-30).
export * from './aiAssistant.js'
export * from './rowOps.js'
export * from './auth.js'
export * from './signInSession.js'
export * from './roleHelpers.js'
export * from './pageBoilerplate.js'
// pageParam — the ONE deep-link parameter reader (D2 §9 "open = exact
// spot"). Registered so every app package and every workspace module
// reaches it with `el.call('readPageParam', 'thread')` — no cross-package
// import, and it resolves before a lazily-folded package merges its own
// context.
export * from './pageParam.js'
// fetchSystemStatus / fetchUptime are shared's own registrations
// (shared/functions/systemStatus.js); the shell does not re-register them.
export * from './homeAiSend.js'
// The assistant's answer mode (Page · Sidebar · Minimized) — `setAssistantDock`
// is reached by el.call from the shared answers choice.
export * from './assistantDock.js'
// The assistant's MODE (Assistant · Dashboard · Apps) — `setAssistantMode` is
// reached by el.call from the mode chip (AI_ASSISTANT_SPEC v1 §1).
export * from './assistantMode.js'
// The docked sidebar's width — clamp, the key steps, the per-person writer
// and its boot reconcile (AI_ASSISTANT_SPEC v1 §8, pick 7 A).
export * from './assistantWidth.js'
// The workspace skin rule (the <html data-skin> stamp and the Appearance card
// read it; el.call-able for any other reader).
export * from './workspaceSkin.js'
export * from './inboundPrompt.js'
export * from './simoneAiTransport.js'
// simone's sendPrompt — read by simone's Chat composer to dispatch a
// user message. Registering here puts it in context.functions so
// `el.call('sendPrompt', text)` resolves to simone's implementation,
// which in turn calls `window.__simoneSendOverBridge` — workspace's
// installSimoneAiTransport hooks that up to sdk.ai.dispatch.
// VENDORED from extensions/packages/simone (private, unpublished) into
// ./_simone/ — the bare `@symbo-ls/simone/...` import broke CI `smbls
// build` and froze every dev.my deploy. In-tree copy stays on the
// workspace tree for esbuild AND resolves in CI.
// Registered as shellSendPrompt: shared owns `sendPrompt` and dispatches to
// the shell's implementation (shared/functions/aiSessionHost.js — ONE SHELL
// ASSISTANT, Nika 2026-10-03).
export { sendPrompt as shellSendPrompt } from './_simone/sendPrompt.js'
export {
  buildExtensionDataBridge,
  auditExtensionSource,
  declaredEntitiesFromSpec,
  extensionEntitySupport,
  BACKED_RECORD_ENTITIES
} from './extensionDataBridge.js'
export {
  saveExtension,
  requestShareExtension,
  confirmShareExtension,
  saveAnswerViewAsApp,
  addAppSavePress,
  addAppSaveStatus
} from './extensionSaveShare.js'
export * from './extensionPreview.js'
// Session manager — reused from simone now that the AssistantDrawer mounts
// the canonical Chat header with its Sessions ▾ history dropdown. Provides
// newSession / switchSession / deleteSession / ensureActiveSession /
// toggleSessionMenu / persistActiveSession (the last snapshots chatMessages
// into root.sessions; simone's sendPrompt fires it each turn). Replaces the
// former no-op stub now that workspace actually has the multi-session UI.
// The five session names shared owns (newSession / switchSession /
// deleteSession / hydrateServerSessions / restoreActiveThread) are registered
// shell-prefixed; shared's dispatcher answers with them inside the shell.
export {
  _toChatMessages,
  ensureActiveSession,
  hydrateServerSessions as shellHydrateServerSessions,
  newSession as shellNewSession,
  openAgentThread,
  restoreActiveThread as shellRestoreActiveThread,
  markThreadTurn,
  switchSession as shellSwitchSession,
  deleteSession as shellDeleteSession,
  toggleSessionMenu,
  persistActiveSession,
  refreshSessionsAfterTurn,
  toggleStarSession,
  shareSession,
  sessionContextItems,
  sessionContextAction
} from './_simone/sessions.js'
export * from './lightbox.js'
// grantModal.js moved to /financials/functions/grantModal.js
export * from './createOrgModal.js'
export * from './whitelabelGate.js'
export * from './usernamePrompt.js'
// The new-account welcome — who sees it, the setup wizard, the assistant's
// conversation, the decision on the user record — and its quick tour
// (components/OnboardingWizard.js, OnboardingChat.js, OnboardingTour.js).
export * from './onboarding.js'
export * from './onboardingWizard.js'
export * from './onboardingTour.js'
// …and its three choices — Your work (a Workspace Preset or Something else),
// Your apps, Your idea (components/OnboardingWorkStep.js, OnboardingStackStep.js,
// OnboardingIdeaStep.js) — plus the app
// builder's `/add-app?prompt=` hand-off (pages/add-app.js).
export * from './onboardingPresets.js'
// …and Fly's ready screen — the one welcome a person who signed up through Fly
// meets (components/OnboardingReadyStep.js).
export * from './onboardingReady.js'
// …and a personalized sign-up's build board — the one step it meets instead of
// the welcome (components/OnboardingBuildStep.js; PLAN §7b-2).
export * from './onboardingBuild.js'
export * from './addAppPrompt.js'
// The builder opens with the focus in its chat composer (persona round 5).
export * from './addAppComposerFocus.js'
// The builder's Preview: the built app, or plainly that the build failed.
export * from './addAppBuildOutcome.js'
// The builder's balance line under its composer.
export * from './addAppBalance.js'
// The cost before a build: the estimate line under the builder's composer.
export * from './addAppEstimate.js'
// The builder's conversation follows its newest message (one scroller).
export * from './addAppChatFollow.js'
// /add-app on a phone: one pane at a time (persona round 5, marcus)
export * from './addAppPhonePane.js'
// A built app's notification opens the app (persona round 5, marcus) — also
// used by _rewriteFetchDecls below.
export { generatedNotifyHref } from './generatedNotifyHref.js'
// …and the CRM visitor line step every built-app mount path runs (one copy).
export { crmLeadsVisitorLine } from './crmLeadsVisitorLine.js'
export * from './addAppPreviewRefresh.js'
// Where a saved app is, at once: its row, the builder's line and its link.
export * from './addAppSaved.js'
export * from './addAppCrmLeads.js'
export * from './shareLinks.js'
export * from './profileModal.js'
export * from './eventDetailModal.js'
export * from './newBookingModal.js'
export * from './ticketDetailModal.js'
export * from './personDetailModal.js'
export * from './personPopover.js'
export * from './partyLookup.js'
export * from './githubColors.js'
export * from './formatUtils.js'
// The ambient sky's helpers (DESIGN-WL-AMBIENT-SKIN-BACKDROP-1).
export * from './ambientBackdrop.js'
// setNavbarTooltipPosition is NOT re-exported here — it now lives solely in
// @symbo-ls/workspace-shared/functions (see that barrel for why). It still
// reaches this scope through the shared library merge, so `el.call(...)`
// keeps working; duplicating it here is what made it shadow shared's copy.
export * from './peopleGroups.js'
export * from './orgChart.js'
export * from './resourceLinks.js'
export * from './pendingInvitations.js'
// loadAnalyticsData + worldSilhouettes moved to
// @symbo-ls/workspace-analytics/functions — they're registered by the
// analytics sub-app's context when /analytics activates.
// Composer '+' menu "Build app" — routes to /add-app + auto-sends the draft
// (root._addAppAutoSend, consumed by pages/add-app.js BuildCanvas).
export * from './forceBuildApp.js'
export * from './aiAssistantThreads.js'
export * from './yourAgents.js'
export * from './agentAssignments.js'
// FALSE-ZERO-HOME-NON-AGENT-KEYS-1 — the Home load-state contract for
// people / workspace-activity / tickets / reminders. See homeLoadState.js
// for why `Array.isArray` is not a loaded signal for an `[]`-seeded key.
export * from './homeLoadState.js'
// Built-in Reminders app (/reminders) — rowToReminder / createReminder /
// toggleReminder / deleteReminder / setRemindersFilter / reminderDueLabel /
// reminderGroups + composer glue. See components/ReminderRow.js,
// components/ReminderComposer.js, pages/reminders.js.
export * from './reminders.js'
export * from './appContextMenu.js'
// Built-in History app (/history) — loadHistoryEvents / historyDays /
// historyPeople / historyTypes / historySummary + the label, date and
// interaction helpers. See components/HistoryTimeline.js, pages/history.js.
export * from './history.js'
// Getting started → Team (/getting-started/team, POB S9) — the found people,
// the owner's invites, the domain switch. See pages/gettingStartedTeam.js.
export * from './gettingStartedTeam.js'
// Getting started → Imports (/getting-started/imports, POB S10) — the import
// connectors, the upload, the preview + mapping, the run and its undo. See
// pages/gettingStartedImports.js.
export * from './gettingStartedImports.js'
// Home "Coming up" widget JOIN button — eventHasMeetLink / joinEventCall.
// See components/IntranetRows.js UpcomingEventCard.JoinBtn.
export * from './eventMeetJoin.js'
// CALL-INVITATION app-wide banner data half — activeMeetingInvite /
// joinMeetingInvite. See components/CallInvitationBanner.js.
export * from './notifications/meetingInviteBanner.js'
// WORKSPACE CALLS — the shell call panel's runtime (comms-phone v2, browser
// calls on the workspace's voice number). Named re-exports only: the pure
// model helpers and constants in the file are not el.call targets. See
// components/WorkspaceCallPanel.js.
export {
  ensureWorkspaceVoice,
  stopWorkspaceVoice,
  acceptWorkspaceCall,
  declineWorkspaceCall,
  hangUpWorkspaceCall,
  toggleWorkspaceCallMute,
  toggleWorkspaceCallHold,
  toggleWorkspaceCallKeypad,
  pressWorkspaceCallDigit,
  requestEndWorkspaceCall,
  cancelEndWorkspaceCall,
  dismissWorkspaceCall,
  workspaceCallEyebrow,
  workspaceCallTitle,
  workspaceCallClock,
  workspaceCallMeta,
  workspaceCallMissed,
  workspaceCallRingMayTakeFocus,
  workspaceCallClear,
  startWorkspaceCall,
  workspaceCallAvailability
} from './calls/workspaceCall.js'

// /admin/flags reads its registry through this ONE function at render time
// (pages/flags.js `children: (el, s) => el.call('featureFlagGroupsWithFlags')`).
// Registered by NAME, not `export *`: featureFlags.js re-exports the whole
// shared registry, and blanket-registering it here would shadow nothing today
// but invites a duplicate-name collision later. See tickets/fable.md for why
// the page may not touch the registry at module scope.
export { featureFlagGroupsWithFlags } from './featureFlags.js'

export * from './notifications/index.js'
export * from './admin/index.js'
export * from './search/index.js'
// /data/external backend-visualization surface — the five Remote* contract
// functions (parseRemotePath / makeRemoteUrl / schemaForRemoteTable /
// saveRemoteRow / deleteRemoteRow) the shared RemoteSchemaList /
// RemoteTableCard / RemoteRowTable / RemoteRowEditor components el.call,
// plus the page's ext* loaders (sources/schema/rows), selection/URL sync,
// custom_http request runner, and fetch-wiring snippet builders.
export * from './externalData.js'
// /admin/module console action helpers (mcLoadState / mcListModuleVersions /
// mcAssignModule / mcSavePersonalization / mcRollbackModule / mcRemoveModule
// + the records-grant editors) — SDK-only, el.call-invoked.
export * from './moduleConsole.js'
// Workspace-app catalog + one-click install for the /add-app rail
// (loadWorkspaceAppsCatalog / installWorkspaceAppRef) — WORKSPACE_APPS.md.
export * from './workspaceApps.js'
// The name a person reads for an installed app — the App Store's catalogue
// name, through the shared store-copy resolver (el.call('installedAppName', app)).
export * from './installedAppName.js'
// THE APP SPLIT — the Workspace apps view's model and actions (docs/APP_SPLIT.md).
export * from './appsHub.js'

// startOAuthFlow — stub. Opens the provider OAuth dialog, persists
// returned tokens to Vault via `integration-admin` upsert. Today throws
// a clear "not wired" message so the `MarketplaceOAuthConnectButton`
// click surfaces a visible error rather than silently failing. DOMAIN's
// first OAuth2 provider (Cloudflare registrar) triggers the full
// implementation; at that point this function gets:
//   1. Build provider OAuth URL + state param encoding {kind, scope, slug}
//   2. window.open in popup / redirect
//   3. Callback handler resolves code → exchange for tokens → Vault store
// Per MARKETPLACE tick-4 REQUEST; scope: workspace/packages/workspace/functions/.
export const startOAuthFlow = (kind, { scopeType, scopeId, slug } = {}) => {
  console.warn(
    `[startOAuthFlow] stub — kind=${kind} scope=${scopeType}:${scopeId ?? 'null'} slug=${
      slug ?? 'default'
    }`
  )
  throw new Error(
    `OAuth flow not wired yet for ${kind} — coming in follow-up once DOMAIN's first OAuth2 provider (Cloudflare registrar) ships.`
  )
}

export * from './closeModal.js'

export * from './searchApps.js'
// Warm a lazy route's chunk while a pointer rests on (or focus reaches) its
// opener — the PageSlot opens without the network round trip.
export * from './slotPrefetch.js'
// The window width, per person (canvas PageSlot's resize handle).
export * from './slotSheetWidth.js'
export * from './searchAppTileMenu.js'
// Global-search-result row context menu (Pin to navbar / Unpin) — see
// navSearchResultMenu.js. Same itemsFn/actionFn contract as
// searchAppTileMenu.js above and shared/functions/navbarTabMenu.js.
export * from './navSearchResultMenu.js'
// startDemo — SDK-only demo provisioning for the demo.symbols.app gate page
// (pages/demo.js). Registered here so the gate can call it via
// `el.call('startDemo', email)` without importing between project files.
export * from './startDemo.js'
// enterDemo — SDK-only ANONYMOUS demo provisioning for the auto-enter /demo
// loader (pages/demo.js). Registered here so the loader can call it via
// `el.call('enterDemo')` without importing between project files. No email gate.
export * from './enterDemo.js'
// demoHandoff — cross-origin sign-in hop demo.symbols.app → my.symbols.app.
// After DemoClaimBanner converts (claimDemoAccount) or clones
// (login + cloneDemoWorkspace) the demo workspace into a real account, the
// banner calls `el.call('demoHandoff')` to redirect the user to the shell's
// hash-token consumer (/auth/magic-callback) authed with the fresh tokens.
export * from './demoHandoff.js'
// Page awareness for the GENERAL workspace AI: buildPageContext(root) snapshots
// the current page for the per-turn dispatch context; selectPageItem(type, id)
// is the el.call companion the AI's select_page_item client tool invokes to open
// a ticket/channel/document/event/room on the page the user is viewing.
// buildAiAwareness is registered shell-prefixed (shared owns the name).
export {
  AI_AWARENESS_PAGES,
  moduleRouteTitle,
  buildSuggestions,
  buildPageContext,
  buildAiAwareness as shellBuildAiAwareness,
  selectPageItem
} from './pageContext.js'

// STRUCTURED AI ATTACHMENTS (spec §4.1) — el.call('attachAiRef' | 'detachAiRef'
// | 'attachAiCanvas' | 'clearAiAttached', …) write the `root.aiAttached` bag
// the drawer composer's chips render and simoneAiTransport forwards as
// `context.attached`. `readAiAttached(root)` is the pure reader every consumer
// normalizes through (the workspace-switch wipe leaves a bare `{}` behind).
export {
  AI_ATTACHED_REF_CAP,
  readAiAttached,
  attachAiRef,
  detachAiRef,
  attachAiCanvas,
  clearAiAttached
} from './aiAttached.js'
// showPoliciesInChat lives in the PUBLISHED GreenIG v2 module
// (silverbreeze-labs/greenig-app — injected into context.functions only for
// that workspace; see functions/workspaceProjects.js).
// AI workspace operations — the assistant performs what the user can do in the
// UI (create/rename/duplicate/delete files, notes, tickets, channels…) by
// calling these via the clientTool path. See aiWorkspaceOps.js.
export * from './aiWorkspaceOps.js'
// start_tour — the assistant starts the quick tour (aiStartTour.js).
export * from './aiStartTour.js'
// contactsDirectory.js (el.call action helpers over the Party Directory
// services) moved to the system/crm-app marketplace module — the shell no
// longer bundles a second copy.

// renderExtensionInPane(iframeNode, { name, source }) — mount an AI-generated
// WORKSPACE EXTENSION into an <iframe> (the /add-app right pane, the home
// dashboard split-pane slot — pages/main.js _mountSplitExtension — and any
// other caller that hands us an iframe node). The cloud agentic loop's
// render_extension_page client tool reaches here via window.__addAppHost →
// injectPageInApp (see simoneAiTransport.js). We parse the model's DOMQL
// source, then boot it as its OWN smbls app inside the iframe via
// createIframeApp.
//
// Still an iframe, and still fully router-isolated — an iframe realm
// physically cannot reach the shell router, so a throwing/misbehaving
// generated page (the AI rebuilds this content on every turn) can never
// blank the workspace (unlike injectPageInApp on the shell root). That
// containment is unchanged.
//
// What DOES change: this is not a foreign embed. Because the iframe is
// same-origin, it's the same JS heap as the shell — so we hand the iframe app
// the WORKSPACE SHELL's full component registry + full function registry via
// createIframeApp's `contextOverrides` (passed BY REFERENCE, no clone — see
// embedComponent.js `shareContext` for the established precedent of this
// exact passthrough; components/functions are never boot-mutated downstream,
// confirmed by reading prepareComponents/prepareContext, so sharing them live
// is safe). The design system is different: we hand over the shell's RAW
// design-system SOURCE (`extensionDesignSystemSource()`, a fresh object every
// render), NOT the shell's live processed CONFIG — see
// functions/extensionDesignSystem.js for the framework input contract that
// makes this the only correct shape, and for why a live reference is unsafe
// on top of that (short version: it wedged the shell in testing). The
// generated DOMQL can therefore extend house components by bare key
// (`extends: 'AppPage'`, brand components), resolve the same workspace design
// tokens the shell resolves (processed once, inside the iframe, against the
// iframe's own document), and call
// house functions via `el.call` — the SAME trust surface a runtime workspace
// module gets (see workspaceProjects.js's context injection). The
// extension's OWN AI-authored components/functions still go through
// createIframeApp's normal clone + rerealm path (isolated, realm-adapted).
//
// This enrichment is opt-in AT THIS CALL SITE ONLY — createIframeApp itself
// is untouched, so canvas embeds / preview / project-app generation (which
// never pass this shape of contextOverrides) resolve byte-identical
// behavior: their own design system, their own context, no workspace
// registry leakage. Scope: workspace-extension rendering only.
//
// The two security gates are unchanged: auditExtensionSource (below) statically
// blocks raw network/storage/eval before the source is ever eval'd, and
// buildExtensionDataBridge scopes the extension's declarative `fetch:` surface
// to exactly its declared workspaceProject.* entities. Returns
// { ok, rendered } / { ok:false, error } so the model can self-correct bad DOMQL.
//
// DESIGN SYSTEM FOR AN EXTENSION IFRAME — two separate hazards, both closed by
// handing over the RAW source (`extensionDesignSystemSource()`), never anything
// derived from the shell's live `ctx.designSystem`.
//
// 1. WRONG INPUT TYPE (2026-07-27). `ctx.designSystem` is the PROCESSED scratch
//    CONFIG; `create()` expects a RAW source and processes it itself. Feeding a
//    processed config back in re-derived every suffix from an already-derived
//    value — the iframe got `--color-black-var`, `--theme-document-background-
//    background`, 3607 var definitions against the host's 1702, and
//    `--theme-document-background` resolving to a
//    `var(--theme-document-<scheme>-background)` that nothing defines, so every
//    `theme:` prop inside an extension was dead. `injectSprites` double-
//    converted the same way (`convertSvgToSymbol` over `<symbol>` strings).
//    See functions/extensionDesignSystem.js for the full contract note and for
//    why the fix belongs here rather than in scratch's transformers.
//
// 2. LIVE-REFERENCE WEDGE (2026-07-13 live test). An earlier version passed the
//    shell's live CONFIG object itself. That wedged the shell into a permanent
//    full-viewport MainLoader the moment an extension was injected
//    (body.smblsapp backref lost, reload required), because `deepClone`
//    (@symbo.ls/utils, packages/utils/object.js) is DELIBERATE about DOM nodes
//    — `if (isDOMNode(value)) { dest[key] = value; continue }` — so the CONFIG's
//    two live DOM bindings, `.themeRoot` (what changeGlobalTheme stamps
//    `data-theme` onto) and `.document`, survived every clone/merge step of
//    smbls' own prepareDesignSystem → createConfig pipeline as opaque leaves.
//    `result.context.designSystem.themeRoot = appEl` (shared/iframeApp/
//    createIframeApp.js) then reached back toward the shell's own theme target.
//    That was patched by stripping the two fields after cloning; the raw source
//    carries neither field to begin with, so the hazard is now absent by
//    construction and the strip is gone with the clone it guarded.
//
// The shell's ACTIVE THEME is still read off the live CONFIG below — but only
// as the `globalTheme` scalar, which is a plain string and carries no DOM
// binding. Everything structural comes from the raw source.
//
// Components/functions are NOT put through this — prepareComponents does a
// plain shallow spread (`{...UIkitWithPrefix(), ...context.components}`, no
// clone) and context.functions is a straight pass-through; neither is
// boot-mutated anywhere in the traced pipeline, so sharing them live is safe.

// The shell's currently active theme name, read off the live scratch CONFIG
// (`changeGlobalTheme` mutates exactly this field — see smbls/CLAUDE.md
// "Global theme handling"), so a user who forced light/dark sees the extension
// pane match rather than just the OS preference.
const _activeGlobalTheme = (ds) => (ds && typeof ds === 'object' && ds.globalTheme) || 'auto'

// Resolve the live workspace root state off the root element, then build the
// iframe seed via the pure helper (functions/extensionDataSeed.js — see there
// for the full why/safety/semantics). Uses smbls deepClone for clone fidelity.
const _seedFromWorkspaceRoot = (source, wsRoot) => {
  const rootState =
    (wsRoot && wsRoot.getRootState && wsRoot.getRootState()) ||
    (wsRoot && wsRoot.state && (wsRoot.state.root || wsRoot.state)) ||
    (wsRoot && wsRoot.getRoot && wsRoot.getRoot() && wsRoot.getRoot().state) ||
    null
  return buildRootStateSeed(source, rootState, { clone: deepClone })
}

// PLATFORM SURFACE — give an AI-generated app the page LAYOUT and the visual
// SURFACE of a native workspace module (fill the frame, comfortable padding,
// scroll, house background + text color) so it doesn't render as cramped bare
// content floating transparent on the build canvas.
//
// The surface is a design-system `theme:` like every other workspace page —
// which is only possible now that the iframe resolves real theme vars. Two
// separate reasons it previously could not: the framework did not inject the
// theme-var block into an embed iframe at all (fixed in smbls 6eb671bc8 +
// 5acd20774), and this file then fed the iframe a double-processed design
// system so the block it did inject named the wrong vars (fixed above). While
// both were broken a `theme:` here resolved to undefined vars — transparent bg
// + black text — and a `_paintHostSurface` helper copied the host's computed
// background/color onto the iframe body instead. That workaround is
// retired: it only ever approximated the surface (it could not touch any OTHER
// `theme:` in the source, which stayed dead), and it tracked the host document
// rather than the extension's own resolved theme.
//
// Merges additively onto the MOUNTED ROOT (only where the source didn't set the
// prop). Mutates + returns the same root object (it IS the shell root;
// wrapping would re-parent its state and break `s.root`/`~/` data binding).
const _applyPlatformSurface = (root) => {
  if (!root || typeof root !== 'object' || Array.isArray(root)) return root
  // House page surface — same token the native workspace pages render on.
  // Respect an app that picked its own theme.
  if (root.theme === undefined) root.theme = 'document'
  // Fill the iframe body (html/body are 100% height) and scroll long content.
  if (root.minHeight === undefined) root.minHeight = '100%'
  if (root.overflowY === undefined && root.overflow === undefined) root.overflowY = 'auto'
  if (root.boxSizing === undefined) root.boxSizing = 'border-box'
  // Comfortable page padding matching workspace pages; respect an app that
  // already manages its own edge spacing.
  if (root.padding === undefined) root.padding = 'C'
  // Stack content vertically by default so a bare page reads top-to-bottom
  // (only when the app didn't pick its own layout direction/display).
  if (root.flow === undefined && root.display === undefined && root.tag !== 'form') {
    root.flow = 'y'
  }
  return root
}

// Empty-list defaults for every `as: '~/key'` fetch target declared in an
// extension source — pre-declared on the mounted page's state so reactive
// readers (children factories above all) TRACK the key from first render and
// re-fire when the fetch lands. A regex false positive merely pre-declares an
// unused [] key; extension-declared state always wins the merge.

// ── IN-SHELL extension rendering (2026-07-25 per Nika: "it does not have to
// be in iframe … building workspace apps should strictly follow workspace
// design system, brand and workspace components … the viewer should render in
// workspace accepted view", same for /add-app) ──────────────────────────────
// Builds a directly-mountable page def from an AI-built app source — NO
// iframe. In-shell mounting is what makes a built app NATIVE: the live design
// system (tokens, themes, dark/light), the house components (Input, Button,
// Select, Flex …) and the active workspace module's components all resolve
// exactly like any workspace page — none of that reached the isolated iframe
// realm (bare unstyled inputs over a transparent surface). Same trust model
// as render_answer_ui, which has always mounted AI-authored defs in-shell:
//   • the static audit gate stays (auditExtensionSource — no raw network/
//     storage/eval/realm-escape primitives);
//   • data access stays ENTITY-SCOPED: every `fetch:` decl in the def is
//     rewritten to carry the extension data bridge's per-decl executor
//     (adapter 'sdk'), so reads/writes still route ONLY through
//     workspaceProject.<declared-entity> — never the full SDK surface;
//   • `as: '~/key'` targets are rewritten to element-local `as: 'key'` —
//     in-shell, `~/` would write the WORKSPACE SHELL's root state (the iframe
//     had its own root). Local `as` writes the page element's state, which is
//     the same state its children factories read (fetch plugin
//     updateElementState). `s.root.<key>` reads now resolve against the LIVE
//     workspace root — no seed copy needed.
// Rewrites, per fetch decl:
//   • `from: 'records'` → `from: 'workspaceProject.records'` — the app
//     vocabulary uses bare entity names; in-shell the decl resolves through
//     the SHELL's sdk adapter, whose dispatcher only knows namespaced routes
//     (verified live: bare 'records' → "[sdk.execute] Unknown entity"). A
//     per-decl scoped executor does NOT work here — the fetch plugin builds
//     the named adapter once and reuses it, so decl-level executor/adapter
//     options are ignored. workspaceProject.* is workspace-pinned +
//     RLS-enforced server-side; trust model = render_answer_ui (in-shell,
//     audit-gated).
//   • `as: '~/key'` → `as: 'key'` — element-local state, not the shell root.
const _rewriteFetchDecls = (definition, seen = new Set(), factories = new WeakMap()) => {
  if (!definition || typeof definition !== 'object' || seen.has(definition)) return
  seen.add(definition)
  if (Array.isArray(definition)) {
    for (const n of definition) _rewriteFetchDecls(n, seen, factories)
    return
  }
  if (definition.fetch && typeof definition.fetch === 'object') {
    const arr = Array.isArray(definition.fetch) ? definition.fetch : [definition.fetch]
    definition.fetch = arr.map((d) => {
      if (!d || typeof d !== 'object') return d
      const out = { ...d }
      if (typeof out.from === 'string' && out.from && !out.from.includes('.')) {
        out.from = `workspaceProject.${out.from}`
      }
      if (typeof out.as === 'string' && out.as.startsWith('~/')) out.as = out.as.slice(2)
      // A notification the app sends opens the app (functions/
      // generatedNotifyHref.js; persona round 5, marcus: a tap on "New
      // open-house lead" opened nothing — the form's data carries no href).
      // The app's own transform runs first; an href it set is kept.
      const entity = typeof out.from === 'string' ? out.from.split('.').pop() : ''
      const method = String(out.method || '').toLowerCase()
      if (entity === 'notifications' && ['insert', 'create', 'upsert'].includes(method)) {
        const own = typeof out.transform === 'function' ? out.transform : null
        out.transform = function (data, element, state) {
          const shaped = own ? own.call(this, data, element, state) : data
          return generatedNotifyHref(shaped, _rootStateOf(element))
        }
      }
      return out
    })
  }
  for (const k in definition) {
    if (k === 'state' || k === 'fetch') continue
    const v = definition[k]
    if (k === 'children' && typeof v === 'function') {
      // Row definitions exist only when the framework invokes the factory.
      // Keep it lazy and rewrite each result before the framework mounts it.
      let wrapped = factories.get(v)
      if (!wrapped) {
        wrapped = function (...args) {
          const children = v.apply(this, args)
          _rewriteFetchDecls(children, new Set(), factories)
          return children
        }
        factories.set(v, wrapped)
        factories.set(wrapped, wrapped)
      }
      definition[k] = wrapped
    } else if (v && typeof v === 'object') _rewriteFetchDecls(v, seen, factories)
  }
}

// ── GENERATED-APP CONTAINMENT (Nika 2026-10-01 "Cut access now") ──────────
// Every AI-generated page is parsed through parseGeneratedAppSource: the AST
// gate (functions/generatedAppContainment.js, rules in generatedAppRules.js —
// the server reads the same file) runs inside parseFreestyleSource on the
// EXACT text it evaluates. A refused page never evaluates and never mounts in
// part; viewers mount generatedAppBlockedDef instead ("This app uses a blocked
// feature" + "Ask AI to rebuild"). auditExtensionSource stays as the second
// line. FAIL CLOSED: if the parser did not run the hook (an older parser that
// ignores the option), the page is refused.
const _rootStateOf = (el) =>
  (el && el.getRootState && el.getRootState()) ||
  (el && el.state && (el.state.root || el.state)) ||
  (el && el.getRoot && el.getRoot() && el.getRoot().state) ||
  null

// generatedAppAllowlist is registered too, so `el.call('generatedAppAllowlist')`
// answers on the served shell (it ignores its `root` argument).
export {
  parseGeneratedAppSource,
  auditGeneratedAppSource,
  generatedAppAllowlist,
  generatedAppSandboxOn,
  buildSandboxedAppDef
}
// The sandbox host's shell wiring (SandboxedApp's Frame calls it).
// Loaded at call time (FA206-style): only SandboxedApp's Frame calls it, and a
// static barrel re-export pulled the whole sandbox host graph into frank's
// global-scope analysis (it never settled; onResize resolved to a canvas body).
export function mountSandboxedApp(...args) {
  return import('./mountSandboxedApp.js').then((m) => m.mountSandboxedApp.apply(this, args))
}

// The page a viewer mounts for a refused app: the blocked-feature state with
// the AI rebuild action. Data only — the copy is the component's.
export const generatedAppBlockedDef = ({ name, violations } = {}) => ({
  extends: 'GeneratedAppBlocked',
  state: {
    appName: String(name || ''),
    blockedReasons: (Array.isArray(violations) ? violations : [])
      .map((v) => (v && v.rule + (v.detail ? ': ' + v.detail : '')) || '')
      .filter(Boolean)
      .slice(0, 12)
  }
})

// The regex audit (auditExtensionSource, the second line) refuses with
// strings; a refusal from either line is the same blocked state.
const _regexRefusal = (audit) => ({
  violations: (audit.violations || []).map((v) => ({ rule: 'audit', at: null, detail: String(v) }))
})

const _blocked = (name, res, prefix = '') => ({
  ok: false,
  blocked: true,
  error: prefix + GENERATED_APP_BLOCKED,
  violations: res.violations,
  def: generatedAppBlockedDef({ name, violations: res.violations })
})

// el.call('buildInlineExtensionDef', { source, name, entities, crmLeads }) → { ok, def }
// `crmLeads` is the listExtensions row's `{ enabled }` (installed apps only).
// The ONE builder every viewer mounts through: the /add-app build canvas, the
// PageSlot preview (openExtensionPreview) and installed /x/<key> app routes.
export function buildInlineExtensionDef(args = {}) {
  const el = this && this.call ? this : arguments[0]
  const a = (this && this.call ? args : arguments[1]) || {}
  const source = String(a.source || '')
  const audit = auditExtensionSource(source)
  if (!audit.ok) return { ..._blocked(a.name, _regexRefusal(audit)), error: audit.error }
  // SANDBOX (ISOLATED-RUNTIME-PLAN §8.1, flag `appsSandbox`, default off):
  // the same gate on the exact text the frame evaluates; nothing evaluates
  // here. The data surface is the caller's (the stored manifest), never the
  // source's own `entities`.
  if (generatedAppSandboxOn(_rootStateOf(el))) {
    const built = buildSandboxedAppDef.call(el, {
      name: a.name || 'Extension',
      source,
      crmLeads: a.crmLeads
    })
    if (built.blocked) return _blocked(a.name, built)
    if (!built.ok) return { ok: false, error: built.error }
    const def = built.def
    setLastRenderedExtension({
      name: a.name || 'Extension',
      source,
      entities: def.sandboxApp.entities
    })
    return { ok: true, sandboxed: true, def, entities: def.sandboxApp.entities }
  }
  const parsed = parseGeneratedAppSource.call(el, source)
  if (parsed.blocked) return _blocked(a.name, parsed)
  if (!parsed.ok) return { ok: false, error: parsed.error }
  const def = parsed.def
  if (!def || typeof def !== 'object') {
    return { ok: false, error: 'source did not evaluate to an object' }
  }
  const pageDef = def.page && typeof def.page === 'object' ? def.page : def
  const ownState =
    pageDef.state && typeof pageDef.state === 'object' && !Array.isArray(pageDef.state)
      ? pageDef.state
      : {}
  pageDef.state = { ..._pageStateSeed(source), ...ownState }
  const entities = [
    ...new Set(declaredEntitiesFromSpec(def).concat(Array.isArray(a.entities) ? a.entities : []))
  ]
  const bridge = buildExtensionDataBridge(entities)
  _rewriteFetchDecls(pageDef)
  // the visitor line under a sign-up form, only while CRM sending is on
  // (the server's row flag; functions/crmLeadsVisitorLine.js)
  crmLeadsVisitorLine(pageDef, a.crmLeads)
  _applyPlatformSurface(pageDef)
  setLastRenderedExtension({ name: a.name || 'Extension', source, entities })
  return { ok: true, def: pageDef, entities: bridge.allowed, support: bridge.support }
}

// el.call('buildInlineMultiPageDef', { name, pages, entities, crmLeads }) → { ok, def }
// Multi-page sibling: audits + parses every page, rewrites their fetch decls
// through ONE shared scoped bridge, and assembles the nav app-shell def
// (multiPageApp.js) for a direct in-shell mount.
export function buildInlineMultiPageDef(args = {}) {
  const el = this && this.call ? this : arguments[0]
  const a = (this && this.call ? args : arguments[1]) || {}
  // The root page's fallback title and the brand word for an unnamed app are the
  // shell's own copy, rendered in the shell — resolved here, where polyglot is.
  const pages = normalizePages(a.pages, { home: el.call('polyglot', 'shell.app.home') })
  if (!pages.length)
    return { ok: false, error: 'at least one page with non-empty source is required' }
  // SANDBOX (ISOLATED-RUNTIME-PLAN §8.1): every page through the same gate on
  // its exact text; nothing evaluates here; the frame builds the nav shell.
  if (generatedAppSandboxOn(_rootStateOf(el))) {
    const sandboxPages = []
    const sandboxEntities = new Set()
    let combinedSource = ''
    for (const p of pages) {
      const audit = auditExtensionSource(p.source)
      if (!audit.ok) {
        return {
          ..._blocked(a.name, _regexRefusal(audit)),
          error: `page ${p.route}: ${audit.error}`
        }
      }
      const checked = auditGeneratedAppSource.call(el, p.source)
      if (checked.blocked) return _blocked(a.name, checked, `page ${p.route}: `)
      if (!checked.ok) return { ok: false, error: `page ${p.route} ${checked.error}` }
      const refused = sandboxComponentRefusals(checked.expr)
      if (refused.length) return _blocked(a.name, { violations: refused }, `page ${p.route}: `)
      for (const e of sandboxWrapperEntities(checked.expr)) sandboxEntities.add(e)
      sandboxPages.push({
        route: p.route,
        title: p.title,
        expr: checked.expr,
        jsonAuthored: checked.jsonAuthored
      })
      combinedSource += '\n' + p.source
    }
    const def = sandboxAppDef({
      name: a.name,
      // the union of every page's OWN wrapper; the caller's list is ignored
      entities: [...sandboxEntities].sort(),
      stateSeed: _pageStateSeed(combinedSource),
      pages: sandboxPages,
      appLabel: el.call('polyglot', 'workspace.apps.nameFallback'),
      crmLeads: a.crmLeads
    })
    setLastRenderedExtension(null)
    return { ok: true, sandboxed: true, def, entities: def.sandboxApp.entities }
  }
  const pageDefs = {}
  const declared = new Set(Array.isArray(a.entities) ? a.entities : [])
  let combined = ''
  for (const p of pages) {
    const audit = auditExtensionSource(p.source)
    if (!audit.ok) {
      return { ..._blocked(a.name, _regexRefusal(audit)), error: `page ${p.route}: ${audit.error}` }
    }
    const parsed = parseGeneratedAppSource.call(el, p.source)
    if (parsed.blocked) return _blocked(a.name, parsed, `page ${p.route}: `)
    if (!parsed.ok) return { ok: false, error: `page ${p.route} ${parsed.error}` }
    const def = parsed.def
    if (!def || typeof def !== 'object') {
      return { ok: false, error: `page ${p.route} did not evaluate to an object` }
    }
    for (const e of declaredEntitiesFromSpec(def)) declared.add(e)
    pageDefs[p.route] = def.page || def
    combined += '\n' + p.source
  }
  const bridge = buildExtensionDataBridge([...declared])
  for (const route of Object.keys(pageDefs)) {
    _rewriteFetchDecls(pageDefs[route])
    crmLeadsVisitorLine(pageDefs[route], a.crmLeads)
  }
  const appShell = buildAppShell({
    name: a.name,
    nav: buildNav(pages),
    pageDefs,
    seedState: _pageStateSeed(combined),
    appLabel: el.call('polyglot', 'workspace.apps.nameFallback')
  })
  setLastRenderedExtension(null)
  return { ok: true, def: appShell, entities: bridge.allowed, support: bridge.support }
}

export async function renderExtensionInPane(iframeNode, args = {}) {
  const source = args && args.source
  if (!iframeNode || !source) return { ok: false, error: 'iframe node and source are required' }
  const wsRoot = (typeof window !== 'undefined' && window.__wsRoot) || null
  if (!wsRoot || typeof wsRoot.call !== 'function') {
    return { ok: false, error: 'no workspace root available' }
  }
  // SANDBOX (appsSandbox): no in-shell iframe mount of generated source.
  if (generatedAppSandboxOn(_rootStateOf(wsRoot))) {
    return {
      ok: false,
      error: 'sandbox: the in-shell mount is off with appsSandbox; use the builders'
    }
  }

  // SECURITY GATE #1 — static audit BEFORE the source is eval'd. Reject any
  // generated extension reaching for raw network/storage/eval/shell-escape
  // primitives (it should only use the declarative fetch: surface). Returns the
  // violations to the caller so the cloud model can self-correct.
  const audit = auditExtensionSource(source)
  if (!audit.ok) return { ok: false, error: audit.error, violations: audit.violations }

  const parsed = wsRoot.call('parseGeneratedAppSource', source)
  if (parsed && parsed.blocked) return _blocked(args.name, parsed)
  if (!parsed || !parsed.ok) return { ok: false, error: (parsed && parsed.error) || 'parse failed' }
  const def = parsed.def
  if (!def || typeof def !== 'object') {
    return { ok: false, error: 'source did not evaluate to an object' }
  }

  const ctx =
    wsRoot.context || (wsRoot.getRoot && wsRoot.getRoot() && wsRoot.getRoot().context) || {}
  const pageDef = def.page || def

  // LIVE-DATA SEED — a drawn answer view reads `s.root.<stateKey>`; the iframe's
  // root state starts empty, so seed the referenced (non-sensitive) workspace
  // root keys into the page def's state before mount. Without this a saved
  // answer view renders blank (see _seedFromWorkspaceRoot). Extension-declared
  // state wins over the seed on a key collision.
  const { seed, seeded, missing } = _seedFromWorkspaceRoot(source, wsRoot)
  if (seeded.length || missing.length) {
    const ownState =
      pageDef.state && typeof pageDef.state === 'object' && !Array.isArray(pageDef.state)
        ? pageDef.state
        : {}
    pageDef.state = { ...seed, ...ownState }
  }

  // FETCH-TARGET STATE PRE-DECLARATION — v3.14 children factories re-run only
  // for state keys PRESENT at first render (the absent-key signal gap,
  // tickets/smbls.md "nested children factory never re-fires…"). An app whose
  // rows land via `fetch: { …, as: '~/suppliers' }` starts with NO `suppliers`
  // key, so the fetch completion filled state that no list re-read — the app
  // rendered an empty table over live data (verified 2026-07-25: iframe state
  // suppliers:3, zero rendered rows). Declare every fetch target up front so
  // the first render tracks the key — the same contract the shell itself
  // relies on (state-app.js declares every reactive key).
  const fetchDefaults = _pageStateSeed(source)
  if (Object.keys(fetchDefaults).length) {
    const ownState =
      pageDef.state && typeof pageDef.state === 'object' && !Array.isArray(pageDef.state)
        ? pageDef.state
        : {}
    pageDef.state = { ...fetchDefaults, ...ownState }
  }

  // Render on the native workspace app-surface (theme/padding/scroll) so the
  // generated app looks like a real module page, not bare content on the
  // canvas. Applied to the mounted root so state binding (`s.root`/`~/`) is
  // untouched.
  _applyPlatformSurface(pageDef)

  // SECURITY GATE #2 — capability-scoped data access. Build a narrowed SDK
  // fetch executor limited to the entities this extension DECLARED (def.entities
  // / spec entities), all forced into the workspace-pinned workspaceProject.*
  // namespace and routed through sdk.execute (RLS-enforced). The extension's
  // declarative `fetch: [{ from: 'policies', method: 'list' }]` resolves through
  // THIS executor — it never sees a raw data client or the full SDK.
  const declaredEntities = [
    ...new Set(
      declaredEntitiesFromSpec(def).concat(Array.isArray(args.entities) ? args.entities : [])
    )
  ]
  const bridge = buildExtensionDataBridge(declaredEntities)

  // Mount the page ONLY as the create() shell (3rd arg). Do NOT also put it in
  // data.app / data.pages['/'] — createIframeApp would then mount both the app
  // shell and the '/' page (the same def) and the extension renders twice.
  //
  // `data` carries ONLY the extension's own (untrusted, AI-authored) content —
  // this is what createIframeApp clones + rerealms into the iframe realm, so
  // any accidental window./document. reference inside an AI-generated factory
  // binds to the IFRAME's globals, never the shell's.

  // The shell's RAW design-system source — a fresh object per render, never
  // anything derived from the live ctx.designSystem CONFIG. See the
  // "DESIGN SYSTEM FOR AN EXTENSION IFRAME" note above. Computed once and
  // reused below so the contextOverrides value and the icon-sprite injection
  // agree on the exact same object.
  const isolatedDesignSystem = extensionDesignSystemSource()

  const data = {
    icons: def.icons || {},
    // Scoped data adapter — the ONLY data channel the extension iframe gets.
    fetch: bridge.fetch,
    // Match the shell's CURRENT active theme (not just OS 'auto').
    globalTheme: _activeGlobalTheme(ctx.designSystem)
  }

  // WORKSPACE-EXTENSION ENRICHMENT — opt-in at THIS call site only (see the
  // function doc-comment above). `contextOverrides` is createIframeApp's
  // existing generic passthrough; nothing in shared/iframeApp changes.
  // components/functions: same-origin iframe = same JS heap, handed over BY
  // REFERENCE — no clone, no rerealm needed (rerealming a trusted shell
  // function would silently re-eval it from source text in the iframe realm,
  // losing its module closure — only extension-authored code in `data` needs
  // that treatment; neither registry is boot-mutated downstream). designSystem:
  // the RAW source computed above, never the live processed CONFIG.
  //
  // Extension-declared components/functions (rare — most generated sources
  // only declare a page) still win on a name collision, mirroring the
  // project-tier-wins-over-shared-tier precedent used everywhere else in this
  // codebase (framework built-ins → shared libraries → project).
  // FUNCTIONS: the allowlisted registry only (generatedAppContainment.js),
  // never the shell's whole ctx.functions — a page that reached
  // `el.call('getSDK')` got the member's full SDK. The page's own audited
  // functions still register.
  const contextOverrides = {
    designSystem: isolatedDesignSystem,
    components: { ...(ctx.components || {}), ...(def.components || {}) },
    functions: {
      ...scopeGeneratedAppFunctions(ctx.functions, parsed.allow),
      ...(def.functions || {})
    }
  }

  try {
    const result = await shellCreateIframeApp(iframeNode, data, pageDef, {
      path: '/',
      resetDocument: true,
      contextOverrides
    })
    if (result) {
      // House icon sprite (Icon component's `icon: 'name'` → <use href="#…">)
      // injected explicitly against the raw design-system source — read-only
      // (confirmed: injectSprites only reads .icons/.svg, never writes back)
      // and RAW is the shape it needs: `convertSvgToSymbol` expects the
      // original `<svg>` body, and scratch's own `setSvgIcon` has already
      // rewritten those to `<symbol>` in a processed CONFIG. `data` no longer
      // carries `designSystem` (moved to contextOverrides above to skip
      // cloning the full registry on every render), so createIframeApp's own
      // internal sprite injection has nothing to key off; do it here instead.
      shellInjectSprites(iframeNode.contentDocument, isolatedDesignSystem)
      // Stash what's now on screen so the Save / Share actions know what to
      // persist (the cloud agent may not have created a Project yet).
      setLastRenderedExtension({
        name: args.name || def.name || 'Extension',
        source,
        entities: declaredEntities
      })
    }
    return result
      ? {
          ok: true,
          rendered: true,
          entities: bridge.allowed,
          support: bridge.support,
          seededKeys: seeded,
          missingKeys: missing
        }
      : { ok: false, error: 'createIframeApp returned null' }
  } catch (e) {
    return { ok: false, error: 'render: ' + ((e && e.message) || String(e)) }
  }
}

// renderMultiPageApp(iframeNode, { name, pages, theme, entities }) — mount an
// AI-generated MULTI-PAGE app in an isolated iframe. Sibling of
// renderExtensionInPane (single page): SAME isolation + data-seed + scoped-fetch
// model, but assembles N parsed pages into a route→pageDef map mounted under a
// generated nav app-shell (multiPageApp.js) so the runtime router navigates
// between them. `pages` is [{ route, title, source }]; the first is forced to '/'.
// The pages map reaches the iframe router via createIframeApp's
// contextOverrides.context passthrough (smbls prepare.js preparePages reads
// `app.routes || context.pages`).
export async function renderMultiPageApp(iframeNode, args = {}) {
  if (!iframeNode) return { ok: false, error: 'iframe node is required' }
  const wsRoot = (typeof window !== 'undefined' && window.__wsRoot) || null
  if (!wsRoot || typeof wsRoot.call !== 'function') {
    return { ok: false, error: 'no workspace root available' }
  }
  // SANDBOX (appsSandbox): no in-shell iframe mount of generated source.
  if (generatedAppSandboxOn(_rootStateOf(wsRoot))) {
    return {
      ok: false,
      error: 'sandbox: the in-shell mount is off with appsSandbox; use the builders'
    }
  }

  const pages = normalizePages(args.pages, { home: wsRoot.call('polyglot', 'shell.app.home') })
  if (!pages.length) {
    return { ok: false, error: 'at least one page with non-empty source is required' }
  }

  const ctx =
    wsRoot.context || (wsRoot.getRoot && wsRoot.getRoot() && wsRoot.getRoot().context) || {}

  // Parse + audit each page; assemble the route→pageDef map + merged registries.
  // Audit gate runs per page (SECURITY GATE #1) — every page's source must pass
  // the same raw-network/storage/eval/shell-escape scan as a single extension.
  const pageDefs = {}
  const mergedComponents = { ...(ctx.components || {}) }
  // The allowlisted registry only — never the shell's whole ctx.functions.
  const mergedFunctions = scopeGeneratedAppFunctions(
    ctx.functions,
    generatedAppAllowlist(_rootStateOf(wsRoot))
  )
  const mergedIcons = {}
  const declaredEntities = new Set(Array.isArray(args.entities) ? args.entities : [])
  let combinedSource = ''
  for (const p of pages) {
    const audit = auditExtensionSource(p.source)
    if (!audit.ok) {
      return { ok: false, error: `page ${p.route}: ${audit.error}`, violations: audit.violations }
    }
    const parsed = wsRoot.call('parseGeneratedAppSource', p.source)
    if (parsed && parsed.blocked) return _blocked(args.name, parsed, `page ${p.route}: `)
    if (!parsed || !parsed.ok) {
      return { ok: false, error: `page ${p.route} ${(parsed && parsed.error) || 'parse failed'}` }
    }
    const def = parsed.def
    if (!def || typeof def !== 'object') {
      return { ok: false, error: `page ${p.route} did not evaluate to an object` }
    }
    pageDefs[p.route] = def.page || def
    // Supplementary components/functions/icons register ADDITIVELY (a page may
    // bring a helper) but never SHADOW a registered one — mirrors renderAnswerUi.
    if (def.components) {
      for (const k in def.components)
        if (!mergedComponents[k]) mergedComponents[k] = def.components[k]
    }
    if (def.functions) {
      for (const k in def.functions) if (!mergedFunctions[k]) mergedFunctions[k] = def.functions[k]
    }
    if (def.icons) {
      for (const k in def.icons) if (!mergedIcons[k]) mergedIcons[k] = def.icons[k]
    }
    for (const e of declaredEntitiesFromSpec(def)) declaredEntities.add(e)
    combinedSource += '\n' + p.source
  }

  // Shared root-state seed — every page mounts under ONE app root, so seed the
  // UNION of the workspace keys any page references (see _seedFromWorkspaceRoot),
  // onto the app-shell's state (= the iframe root state all pages read via
  // `s.root.<key>`). Fetch `as:` targets from EVERY page pre-declare alongside
  // (same absent-key tracking gap as the single-page path — see
  // _pageStateSeed); real seeds win over its defaults on collision.
  const { seed, seeded, missing } = _seedFromWorkspaceRoot(combinedSource, wsRoot)
  const seedState = { ..._pageStateSeed(combinedSource), ...seed }

  const nav = buildNav(pages)
  // The shell embeds the parsed page defs as hide-gated children and switches on
  // root `_appRoute` (state-based nav — the framework URL router doesn't fire
  // through this embed path; see multiPageApp.js). The seed rides on the shell's
  // root state so every page's `s.root.<key>` reads resolve.
  const appShell = buildAppShell({
    name: args.name,
    nav,
    pageDefs,
    seedState,
    appLabel: wsRoot.call('polyglot', 'workspace.apps.nameFallback')
  })

  const bridge = buildExtensionDataBridge([...declaredEntities])
  const isolatedDesignSystem = extensionDesignSystemSource()
  const data = {
    icons: mergedIcons,
    // Scoped data adapter — the ONLY data channel the app iframe gets.
    fetch: bridge.fetch,
    globalTheme: (args.theme && String(args.theme)) || _activeGlobalTheme(ctx.designSystem)
  }
  const contextOverrides = {
    designSystem: isolatedDesignSystem,
    components: mergedComponents,
    functions: mergedFunctions
  }

  try {
    const result = await shellCreateIframeApp(iframeNode, data, appShell, {
      path: '/',
      resetDocument: true,
      contextOverrides
    })
    if (result) {
      shellInjectSprites(iframeNode.contentDocument, isolatedDesignSystem)
    }
    return result
      ? {
          ok: true,
          rendered: true,
          routes: pages.map((p) => p.route),
          entities: bridge.allowed,
          support: bridge.support,
          seededKeys: seeded,
          missingKeys: missing
        }
      : { ok: false, error: 'createIframeApp returned null' }
  } catch (e) {
    return { ok: false, error: 'render: ' + ((e && e.message) || String(e)) }
  }
}

// loadAddAppExtensions(root) — populate the /add-app sidebar (root._userApps)
// with this workspace's persisted AI-built extensions (+ their DOMQL source) via
// sdk.ai.listExtensions. Called from add-app.js onCreate, so created extensions
// appear in the sidebar and SURVIVE RELOAD: clicking an item re-renders the
// persisted source into the pane (window.__addAppHost). SDK-only backend access.
export async function loadAddAppExtensions(root, _attempt = 0) {
  if (!root || typeof root.update !== 'function') return
  try {
    const ai = shellGetAiService()
    const wsId = activeWorkspaceId(root)
    // On mount the shell may still be wiring the SDK / resolving the active
    // workspace from the governance JWT claims. Without BOTH, listExtensions
    // comes back empty and the sidebar looks permanently appless (created
    // extensions silently missing). Retry briefly until both are ready rather
    // than settling on an empty list. ~12 × 400ms ≈ 5s before giving up.
    if (!ai || typeof ai.listExtensions !== 'function' || !wsId) {
      if (_attempt < 12) {
        setTimeout(() => {
          loadAddAppExtensions(root, _attempt + 1)
        }, 400)
        return
      }
      root.update({ _userApps: [] })
      return
    }
    if (typeof ai.updateContext === 'function') ai.updateContext({ activeWorkspaceId: wsId })
    const exts = await ai.listExtensions({ workspaceId: wsId })
    const arr = Array.isArray(exts) ? exts : []
    // The apps rail is a page sidebar (pages/add-app.js): it shows unless the
    // person hid it for the page, so the list is all this sets.
    root.update({ _userApps: arr })
  } catch (err) {
    console.warn('[add-app] loadAddAppExtensions failed', err)
    // A transient failure (e.g. token not primed yet) — retry before settling.
    if (_attempt < 12) {
      setTimeout(() => {
        loadAddAppExtensions(root, _attempt + 1)
      }, 400)
      return
    }
    root.update({ _userApps: [] })
  }
}

// selectAddAppExtension(ext) — pick an extension in /add-app: bind the builder
// chat to its PROJECT-scoped thread (load THAT extension's history into the
// _ax_* context) AND re-render its source in the build pane. One extension
// (= Project) = one thread; the general drawer/composer are untouched.
export async function selectAddAppExtension(ext) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root || !ext || !ext.id) return
  // Stamp the id we're hydrating BEFORE any async work starts — a plain root
  // property, not root.update (bookkeeping, not reactive UI state; same
  // convention as root._slotResolve/_slotOnClose in pages/main.js). Lets
  // hydrateAddAppThread (below) and the URL-sync reactor in pages/add-app.js
  // tell "already hydrated this id" apart from "a bare _addAppProjectId write
  // that still needs hydrating" — without it, a deep-link/Edit-menu arrival
  // that lands here would redundantly re-poll + re-select the same extension.
  root.__addAppHydratedThreadId = String(ext.id)
  // `_addAppSwitching` drives a LOADING state (skeleton) in both panes for the
  // duration of the switch — instead of flashing the empty "renders here"
  // placeholder + empty chat while the thread + source load. Cleared when the
  // render settles (AppRender host callback) or immediately here when the
  // selected extension has no source to render. Reset _addAppRendered so the
  // stale previous frame doesn't linger. _addAppScope follows the extension's
  // OWN scope so a deep-linked/Edit-menu SHARED extension lands with the
  // Shared tab active (and itself visible in that filtered sidebar list)
  // instead of silently sitting under a tab that filters it out.
  root.update({
    _addAppProjectId: String(ext.id),
    _addAppScope: ext.scope || 'private',
    _addAppSwitching: true,
    _ax_chatMessages: [],
    _ax_activeSessionId: null,
    _addAppRendered: false
  })
  // Load the extension's project-scoped conversation list into _ax_sessions.
  try {
    await el.call('hydrateServerSessions', 'addapp')
  } catch (_) {}
  // Open the most-recent thread — load its messages into the _ax_ buffer.
  try {
    const sess = root._ax_sessions || {}
    const top = Object.values(sess)
      .filter((v) => v && typeof v === 'object')
      .sort((a, b) => (b.ts || 0) - (a.ts || 0))[0]
    const ai = shellGetAiService()
    if (top && top.id && ai && typeof ai.getConversation === 'function') {
      const data = await ai.getConversation(top.id, { projectId: String(ext.id) })
      // The same reader a reload uses (_simone/sessions.js restoreActiveThread):
      // a saved tool call folds into its NAMED timeline row with its result —
      // the bare per-message rows this used to build turned every saved
      // `role: 'tool'` result into a step read out as "Tool", fourteen times
      // over (sam, round 1b) — machine trailers and the builder's own notes
      // stay out of the bubbles, and a turn the server refused for credits
      // reads as the shell's own sentence (functions/creditsRefusal.js).
      const rows = el.call('_toChatMessages', (data && data.messages) || []) || []
      const worded = el.call('creditsRefusalRows', rows, root)
      const msgs = Array.isArray(worded) ? worded : rows
      root.update({ _ax_chatMessages: msgs, _ax_activeSessionId: top.id })
    }
  } catch (_) {
    /* fresh / empty thread */
  }
  // Re-render the selected app in the build pane. Multi-page apps (create_app)
  // re-render via renderMultiPageApp with their persisted pages; single-page
  // extensions via injectPageInApp with their source.
  let willRender = false
  try {
    const host = typeof window !== 'undefined' && window.__addAppHost
    if (ext.appKind === 'multipage' && Array.isArray(ext.pages) && ext.pages.length && host) {
      willRender = true
      window.__addAppHost.call('renderMultiPageApp', {
        name: ext.name,
        pages: ext.pages,
        theme: ext.theme || null
      })
    } else if (ext.source && host) {
      willRender = true
      window.__addAppHost.call('injectPageInApp', { name: ext.name, source: ext.source })
    }
  } catch (err) {
    willRender = false
    console.warn('[add-app] render selected extension failed', err)
  }
  // Nothing will render (no source / no host) → settle the loading state now;
  // otherwise the AppRender host callback clears it when the frame mounts.
  if (!willRender) root.update({ _addAppSwitching: false })
}

// consumeAddAppOpenId(root) — when /add-app is opened by "open this AI app"
// (the /studio tile dblclick for a source-based app sets window.__addAppOpenId
// then navigates here — those apps would open BLANK in the frank canvas editor),
// select + render that app once loadAddAppExtensions has populated _userApps.
// Polls briefly because loadAddAppExtensions retries until the SDK + workspace
// are ready. Called from the /add-app page onCreate.
export function consumeAddAppOpenId(root) {
  if (typeof window === 'undefined' || !window.__addAppOpenId) return
  const self = this
  let attempt = 0
  const tryOpen = () => {
    const id = window.__addAppOpenId
    if (!id) return
    const ext = ((root && root._userApps) || []).find((a) => String(a.id) === String(id))
    if (ext) {
      window.__addAppOpenId = null
      try {
        self.call('selectAddAppExtension', ext)
      } catch (_) {
        /* best-effort */
      }
      return
    }
    attempt += 1
    if (attempt < 25) setTimeout(tryOpen, 300)
    else window.__addAppOpenId = null // give up — the app isn't in this list
  }
  tryOpen()
}

// hydrateAddAppThread(root) — resolve + load the /add-app thread for TWO
// entry paths that can land a project id on root WITHOUT going through
// selectAddAppExtension directly (which needs the FULL extension record —
// source/pages/name — not just an id, to actually load messages + render):
//   1. A deep-linked / reload-restored `?thread=<projectId>` URL param — see
//      pages/add-app.js's BuildCanvas, which mirrors _addAppProjectId back
//      onto the URL (history.replaceState) so every thread gets a shareable,
//      restorable link.
//   2. root._addAppProjectId already set BEFORE (or as) this page mounts —
//      e.g. the apps-launcher "Edit" action (functions/searchAppTileMenu.js)
//      pre-seeds the id with `root.update({ _addAppProjectId }, {
//      preventFetch: true })` then navigates here.
// root._addAppProjectId wins when both are present — a deliberate "open this
// extension" click is a fresher signal than a URL that may be stale. Polls
// root._userApps the same way consumeAddAppOpenId does (loadAddAppExtensions
// populates it async, so the id may not resolve on the very first tick);
// re-derives the wanted id on every tick so a click elsewhere mid-poll is
// picked up instead of stubbornly finishing on a stale target.
//
// No-ops instantly once selectAddAppExtension has already hydrated the SAME
// id (root.__addAppHydratedThreadId) — safe to call unconditionally from both
// onCreate (fresh mount) and onUpdate (subscribeTo _addAppProjectId, for an
// arrival that reuses the already-mounted page) without ever double-hydrating
// a plain sidebar click.
//
// Not found once the list settles (deleted / not visible to this member —
// listExtensions() IS the access boundary, so "missing from _userApps"
// already means "no access"): clear the id + the stale `?thread=` so the page
// degrades to the normal blank/new-thread placeholder instead of hanging on
// a phantom id.
//
// `opts.url === false` — the builder is not the route (the home's Build tab,
// functions/homeBuild.js): the address bar is another page's, and its
// `?thread=` may be mail's or chat's, so it is neither read nor cleared.
export function hydrateAddAppThread(root, opts) {
  if (!root) return
  const self = this
  const useUrl = !(opts && opts.url === false)
  let attempt = 0
  const tryHydrate = () => {
    const fromUrl =
      useUrl && typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('thread')
        : null
    const wanted = (root._addAppProjectId && String(root._addAppProjectId)) || fromUrl
    if (!wanted || root.__addAppHydratedThreadId === wanted) return
    const ext = (root._userApps || []).find((a) => a && String(a.id) === wanted)
    if (ext) {
      try {
        self.call('selectAddAppExtension', ext)
      } catch (err) {
        console.warn('[add-app] hydrateAddAppThread select failed', err)
      }
      return
    }
    attempt += 1
    if (attempt < 25) {
      setTimeout(tryHydrate, 300)
      return
    }
    // Give up — only clear if nothing else (a click elsewhere during the
    // poll) already moved root on to a different id.
    if (String(root._addAppProjectId || '') === wanted && root.update) {
      root.update({ _addAppProjectId: null })
    }
    if (useUrl && typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href)
        if (url.searchParams.get('thread') === wanted) {
          url.searchParams.delete('thread')
          window.history.replaceState(null, '', url.toString())
        }
      } catch (err) {
        console.warn('[add-app] hydrateAddAppThread url clear failed', err)
      }
    }
  }
  tryHydrate()
}

// Dev-only workspace-module override control.
//   el.call('setWorkspaceModuleDev', { owner, key, channel })  — pin to the ACTIVE workspace
//   el.call('clearWorkspaceModuleDev')                          — remove it
// Registered by NAME (not `export *`) so workspaceProjects.js's internals —
// _resolveModuleLoader, _guardPage, _concurrencyGate, _normalizeManifest,
// _normalizeOpens, _mergeFilesCaps, getModulePageDef, the __*ForTests hooks —
// stay out of the function registry where they could shadow something
// (Rule 63). Every one of these is consumed only via a real sibling import
// within workspace/packages/workspace (pages/main.js's getModulePageDef
// read, workspaceProjects.js's own _mergeFilesCaps call), never a bare
// `el.call` — frank's dropped-export check flags them anyway (it can't tell
// "deliberately excluded" from "forgot"), so this comment is the exception
// list it can't read.
//
// The setter exists so the override can never be written unpinned again: an
// unpinned key applies to EVERY workspace and silently replaces real tenant
// content, which is how Bellforge rendered the AI House module. See
// _devModuleOverride in workspaceProjects.js.
export { setWorkspaceModuleDev, clearWorkspaceModuleDev } from './workspaceProjects.js'
// Public-shaped loaders/accessors (no underscore prefix, unlike the
// deliberately-excluded internals two exports above) — same barrel-gap class
// as `redirectIfExternalOnly` further up this file: any of these that gets
// referenced BARE from a function frank promotes to globalScope (because
// that caller itself has an unrelated unresolved free var) needs to resolve
// there too, or the promoted copy throws a guaranteed ReferenceError at call
// time. See tickets/fable.md "decompose workspace-shell's 507 defects by
// owning package" — `frank to-json`'s dropped-export check found these four
// (plus 138 more across 32 other files, fixed the same way below) missing
// from the emitted `functions` bundle.
export {
  getAppsLoadOutcome,
  getModuleLoadOutcome,
  loadWorkspaceProject,
  loadWorkspaceApps,
  whenAppRouteReady,
  updateWorkspaceActivation,
  settleWorkspaceActivation,
  workspaceActivationView,
  moduleRailActive,
  moduleImmersiveActive,
  moduleImmersiveLanding,
  moduleViewAsKey,
  moduleViewAsActive,
  readSlotFullscreenPref,
  writeSlotFullscreenPref,
  slotFullscreenActive,
  readModuleChromePref,
  writeModuleChromePref
} from './workspaceProjects.js'

// ── dropped-export barrel-gap fix (tickets/fable.md, 2026-08-09) ──
// `frank to-json`'s verifyEmission CHECK 4 walks every top-level `export`
// under functions/ and flags any name absent from the emitted `functions`
// bundle — this project's barrel deliberately curates by NAME rather than
// blanket `export *` (see the `featureFlagGroupsWithFlags` / `permissions.js`
// notes above), so a file can gain a new top-level export without anyone
// remembering to also list it here. `redirectIfExternalOnly` (above) was a
// REAL instance of this: a silent-undefined `el.call` that skipped a
// security-relevant redirect. These 138 names, across the 32 files below,
// are the same class of gap — none carry a documented "keep this out of the
// registry" rationale (contrast workspaceProjects.js's Rule-63 hedge just
// above), so all are safe, additive barrel exports.
export {
  handleBridgePayload,
  handleAgentEvent,
  wsTransport,
  createDirectWsPort
} from './_simone/wsBridge.js'
export {
  MAX_SNAPSHOT_AGE_MS,
  MAX_SNAPSHOT_ENTRIES,
  decodeAccessTokenUserId,
  _snapshotKey,
  writeBootSnapshot,
  readBootSnapshot,
  purgeBootSnapshot,
  purgeAllBootSnapshots,
  _snapshotMatchesUrlScope,
  resolveWarmBootSnapshot,
  readSessionBootOrgs
} from './bootSnapshot.js'
export { CANVAS_REDIRECT_RESERVED_SEGMENTS, isCanvasProjectPath } from './canvasProjectRedirect.js'
export {
  orgSwitchStaleWorkspaces,
  orgSwitchUrlWorkspace,
  orgSwitchHopWorkspace
} from './orgSwitchUrl.js'
// claimResolver.js is imported directly by its callers; nothing el.calls
// 'resolveClaims', and canvas and the shell each held a different resolver
// under that one key (a catalog merge kept whichever library came first).
export { selectNonCoreMembers, deactivateNonCoreMembers } from './deactivateNonCoreMembers.js'
// /admin/danger — who may purge and deactivate, from the server's own rules
// (CRUD-CONFIRM-STANDARD D10, 2026-10-30).
export { adminDangerRoleBlock } from './adminDangerRights.js'
// /admin/teams — rename, delete and the permission self-guard, behind the
// house dialogs (CRUD-CONFIRM-STANDARD, 2026-10-30).
export * from './adminTeamActions.js'
export { isDemoHost } from './demoHost.js'
export { isPlatformHost, isCustomDomainHost } from './customDomainHost.js'
// The sign-in route wrappers' title on a white-label host (pages/index.js).
export { authWrapperTitle } from './authWrapperTitle.js'
export {
  isPlatformHostname,
  pathNamesOrg,
  resolveCustomDomainPath,
  bootCustomDomainScope
} from './customDomainBoot.js'
export { DEMO_BLOCKED_ROUTE_RE, demoRouteGuard } from './demoRouteGuard.js'
// The comm host's lockdown (comm.symbols.app — docs/COMM_HOST.md); the host
// rule and the route list are shared (functions/commHost.js there).
export { commRouteGuard } from './commRouteGuard.js'
export {
  SEED_DENY_KEYS,
  SEED_DENY_RE,
  referencedRootKeys,
  buildRootStateSeed
} from './extensionDataSeed.js'
export {
  workspaceDesignSystemSource,
  extensionDesignSystemSource
} from './extensionDesignSystem.js'
export { setLastRenderedExtension, getLastRenderedExtension } from './extensionSaveShare.js'
export {
  USE_FINANCIALS_PROJECT,
  USE_MULTITENANT,
  DUAL_WRITE_FINANCIALS,
  USE_PROFILES_OVERLAY,
  USE_ONBOARDING_GATE,
  hydrateFeatureFlags,
  setFeatureFlag,
  setAppAccessEnforced
} from './featureFlags.js'
export { getWorkspaceCached, invalidateWorkspaceCache } from './getWorkspaceCached.js'
// A page that DECLARES unsaved work, and the one "Discard changes?" prompt —
// the PageSlot keep-alive cap and the split-pane replace both el.call them.
export { hasUnsavedInput } from './hasUnsavedInput.js'
export { confirmDiscardChanges } from './confirmDiscardChanges.js'
// A page's save succeeded: its field-kit controls stop declaring unsaved work.
export { markPageSaved } from './markPageSaved.js'
export {
  creditGuardState,
  creditBlockerShown,
  creditShortState,
  askAdminsForCredits,
  creditPillVisible,
  creditDockMode,
  loadCreditGuard,
  notifyCreditThreshold,
  notifyCreditsExhaustedFromMessage,
  CREDITS_EXHAUSTED_ERROR_CODES,
  creditBlockerDismissedStorageKey,
  readCreditBlockerDismissed,
  writeCreditBlockerDismissed
} from './creditGuard.js'
// The shell's own sentence for a turn the server refused for credits — registered
// steps, reached as `el.call('creditsRefusalRow' | 'creditsRefusalRows', …)` by the
// state layer (sessions.js, simoneAiTransport.js) and by selectAddAppExtension above,
// so none of them imports it.
export {
  isCreditsError,
  creditsShortfall,
  creditsErrorText,
  applyCreditsError,
  creditsRefusalRow,
  creditsRefusalRows
} from './creditsRefusal.js'
// Same barrel-gap class as the 138-name block below, re-opened by the
// helper modules extracted out of app.js / pages/main.js after 2026-08-12
// (`untrackedState.js`, `moduleNotifyBridge.js`) and by `homePanelSet.js`
// gaining a second export next to the one already listed here. Each is
// public-shaped (no `_` prefix), has real sibling importers in this package,
// and carries no "keep it out of the registry" rationale — so all are safe,
// additive barrel exports. (The GridStack-era home helpers — gridInitRearm,
// homeGridPlacement, homeAutolayoutFill — retired with the engine,
// DESIGN-WL-HOME-WIDGETS-1.)
export { resolveHomePanelSet, appDefaultWidgetIds } from './homePanelSet.js'
export { untrackedState } from './untrackedState.js'
//
// `withLiveQuery` / `queryHashTail` (functions/bootRedirectQuery.js) are the
// DELIBERATE exclusions from that same wave, for the Rule-63 reason
// functions/featureFlags.js documents at length: packages/shared has its own
// bootRedirectQuery.js (a deliberate copy, not a stale duplicate — see the
// header comment in each) and its barrel does `export * from
// './bootRedirectQuery.js'`, so BOTH names are already registered through
// sharedLibraries. Listing this package's copies here would register a second
// identical binding under the same key. Both are consumed only via real
// sibling imports (app.js, functions/auth.js, functions/demoRouteGuard.js),
// never a bare `el.call`. frank's dropped-export check flags them anyway.
export { bootMark, bootMeasure, IDLE_BOOT_TIMEOUT_MS, deferIdleBoot } from './idleBoot.js'
export {
  hasAuthoritativeWorkspace,
  awaitInitialWorkspace,
  createPostAuthWorkspaceRevealCoordinator
} from './initialWorkspaceReveal.js'
export {
  LAUNCHER_FALLBACK_ICON,
  LAUNCHER_ICON_BGS,
  titleFromRoute,
  normalizeLauncherTile,
  fillLauncherTileFrom
} from './launcherTiles.js'
// __setWorkspaceChangeWatchdogMsForTests is a deliberate Rule-63 exclusion —
// same `__*ForTests` convention as workspaceProjects.js's test-shrink hooks
// below (test-only mutable-timer knob, not a runtime API any consumer
// should reach via `el.call`).
//
// beginPostAuthWorkspaceReveal / isPostAuthWorkspaceReveal /
// endPostAuthWorkspaceReveal are DELIBERATELY excluded too, for a different
// reason: each is `_postAuthWorkspaceReveal.begin/isActive/end`, a closure
// bound to ONE `createPostAuthWorkspaceRevealCoordinator({ beginDataLoad,
// endDataLoad })` instantiation below. Barrel-exporting them (tried during
// the tickets/opus.md frank sweep, reverted) makes frank re-extract each as
// an independently-reachable unit — and a closure over an ENCLOSING
// factory's parameters cannot be re-attached to anything at revival time;
// renaming the param didn't help, the class is the same
// "factory-closure-capture" bug already open + explicitly NOT rushed for
// canvas's PageSlot (72 findings, same ticket). The coordinator FACTORY
// itself (`createPostAuthWorkspaceRevealCoordinator`, exported above) is
// self-contained and frank-clean; only these three bound-instance
// derivatives are the exception. Consumed today only via the real sibling
// import in auth.js — never a bare `el.call`.
export {
  beginDataLoad,
  endDataLoad,
  endDataLoadFailsafe,
  _person,
  _dashboardPrefsFetches,
  registerWorkspaceConfigConverge,
  loadPrivateData,
  retryWorkspaceActivation,
  scopeGen,
  scopeStale,
  endWorkspaceSession,
  onWorkspaceChange
} from './loadPrivateData.js'
export { MODULE_ACTION_FN_NAMES, buildModuleActionFns } from './moduleActions.js'
// The record-route registry — "where does a row of this collection open?".
// Read through `el.call('moduleRecordRoute', collectionKey)` from any surface
// that has to open a `ref:record:` (design §3.4). The registry is built from
// what each installed module DECLARES; no module name lives in the shell.
export { moduleRecordRoutes, moduleRecordRoute } from './moduleRecordRoutes.js'
// The record-row registry — "which of the app's own components draws one row
// of this collection?" (manifest `collections[].row`). Read through
// `el.call('moduleRecordRow', collectionKey)`; the AI answer's records list
// mounts that row (functions/assistantRecords.js assistantRecordsRowMount).
export { moduleRecordRow } from './moduleRecordRows.js'
export {
  buildModuleAiBridge,
  buildModuleAiStreamBridge,
  buildModuleAiAdminBridge
} from './moduleAiBridge.js'
export { buildModuleRegistryBridge } from './moduleRegistryBridge.js'
export { buildModuleCalendarBridge } from './moduleCalendarBridge.js'
export { buildModuleNotifyBridge, buildModuleNotifyCloseBridge } from './moduleNotifyBridge.js'
export { buildModuleDataBridge } from './moduleDataBridge.js'
// The PER-TILE moduleData (USER-CODE-ORIGIN security review B1): this
// project's own grant, pinned to the workspace it is installed in.
export { tileModuleDataBridge } from './tileModuleDataBridge.js'
export {
  buildModuleFileListBridge,
  buildModuleFileReadBridge,
  buildModuleFileBridge,
  buildModuleFileCreateBridge,
  buildModuleFileUpdateBridge,
  _storageRefFromUrl
} from './moduleFileBridge.js'
export {
  resolveModuleUrl,
  resolveAssetUrl,
  normalizeDependencies,
  peerPins,
  buildModuleImportBridge,
  buildModuleMountBridge,
  buildModuleApiBridge,
  buildModuleTokenBridge,
  buildModuleUnmountBridge,
  buildModuleAssetUrlBridge,
  buildModuleThemeBridge
} from './moduleImportBridge.js'
export { buildModuleOpenBridge } from './moduleOpenBridge.js'
export { cleanRoute, normalizePages, buildNav, buildAppShell } from './multiPageApp.js'
export { mwtRouter, reconcileMwtOrgFromUrl, mwtOnRenderRouter } from './mwtRouter.js'
export { NotificationWrapper } from './notifications/collab/components.js'
// notifications/collab/queries.js (listNotifications / markRead / markAllRead)
// is NOT registered: nothing el.calls those keys for notifications (the
// panel imports queries.js directly; shared owns markNotificationRead /
// markAllNotificationsRead and listNotifications). Registered, the shell's
// `markRead(notifId)` shadowed chat's `markRead(channelId)` — the shell's own
// openChatFromRef (pageContext.js) marked a NOTIFICATION with a channel id.
export { primePeople, primedOrgSlug, peekPrimedPeople, consumePrimedPeople } from './peoplePrime.js'
export {
  APP_ACCESS_VERBS,
  APP_ACCESS_APPS,
  APP_PERMISSIONS,
  PERMISSIONS,
  PERMISSION_KEYS,
  PERMISSION_GROUPS,
  ORG_ROLE_PERMISSIONS,
  SYSTEM_TEAMS,
  SYSTEM_TEAM_SLUGS,
  CATEGORIES,
  CATEGORY_PERMISSION_TEMPLATES,
  teamMatchesTemplate,
  dominantCategory,
  LEGACY_ORG_IDS,
  PERMISSIONS_BY_GROUP,
  hasPerm,
  claimsIsActiveMember,
  isInTeam,
  claimsIsOwner,
  claimsIsAdmin,
  canAccessGovUI,
  effectiveCategory,
  permLabel,
  permDescription,
  permGroup,
  PERMISSION_GROUP_KEYS,
  permLabelText,
  permDescriptionText,
  permGroupText
} from './permissions.js'
export { activeWorkspaceId, hasActiveWorkspace } from './scopedClient.js'
export { buildUiCatalog } from './uiCatalog.js'
export {
  DEFAULT_NAVBAR,
  DEFAULT_DESIGN_SYSTEM,
  DEFAULT_CONFIG,
  shellDeepEqual as deepEqual,
  resolveWorkspaceConfig,
  applyWorkspaceConfig,
  resolveDesignSystemVars,
  watchDesignSystemTheme,
  // the ONE reader of installed app entries (never the records-grant row) —
  // registered so a self-contained function reaches it with el.call
  installedAppRefs
} from './workspaceConfigs.js'
export {
  homeWidgetHue,
  homeWidgetSurfaceHue,
  homeWidgetColorOptions,
  homeWidgetAppColorOption,
  posterFamilyEntry,
  POSTER_FAMILY_KEYS,
  POSTER_FAMILY_LABELS,
  rowHueRotation
} from './homeWidgetHue.js'
export { homeWidgetColorsKey, readHomeWidgetColors } from './homeWidgetColors.js'
// The home board's one geometry table (HOME_WIDGETS_SPEC.md §1) and the
// widget visuals' geometry — registered so the home components reach them
// through el.call.
export {
  homeGridMetrics,
  homeWidgetSpan,
  homeWidgetSpanAt,
  homeSpanVarsFor,
  homeSpanFitWords,
  homeSpanVar,
  homeSpanFitCss,
  homeWidgetSizeForCell
} from './homeGridMetrics.js'
export { homeSparkGeom, homeSlotText } from './homeWidgetVisuals.js'
// The (?) panel's placement at its glyph (components/HomeWidgetFrame.js).
export {
  homeHelpTipPlace,
  homeHelpTipReserve,
  homeWidgetHelpTipMeasure,
  homeWidgetHelpTipForget,
  homeWidgetHelpTipStyle,
  homeWidgetHelpTipClose,
  homeWidgetHelpOpen
} from './homeWidgetHelpTip.js'
// The home widget frame's helpers and drag-to-reorder (components/HomeWidgetFrame.js).
export {
  homeWidgetAccent,
  homeWidgetAccentVars,
  homeWidgetAccentOnSurfaceVars,
  homeWidgetIsApp,
  homeWidgetDefaultSize,
  homeWidgetFrameSize,
  homeWidgetBaseSize,
  homeWidgetSpanAtOf,
  homeWidgetSpanFor,
  homeWidgetSpanVars,
  homeWidgetSpanFit,
  homeWidgetFrameStyle,
  homeWidgetFrameOrder,
  homeWidgetFrameHidden,
  homeWidgetUnderView,
  homeWidgetOpenRoute,
  homeWidgetActionOf,
  homeWidgetOpen,
  homeWidgetRunAction,
  openHomeWidgetForm,
  mountHomeWidgetForm,
  homeWidgetHelpOf,
  homeWidgetHideLabel,
  homeWidgetParams,
  homeWidgetSizeRows,
  homeWidgetSizeMenuRow,
  homeWidgetSetSize
} from './homeWidgetFrame.js'
export {
  homeWidgetDragStart,
  homeWidgetDragMove,
  homeWidgetDragEnd,
  homeWidgetKeyMove
} from './homeWidgetDrag.js'
// The corner-handle resize (snap to S · M · L · XL, never below the minimum)
// and the board's motion (FLIP reflow, grow reveal, exit) —
// DESIGN-WL-HOME-WIDTH-RESIZE-1.
export {
  homeWidgetResizeSizes,
  homeWidgetResizeBox,
  homeWidgetResizeSnap,
  homeWidgetResizeStep,
  homeWidgetResizeMetrics,
  homeWidgetResizeStepBy,
  homeWidgetResizeStart,
  homeWidgetResizeMove,
  homeWidgetResizeEnd,
  homeWidgetResizeKey,
  homeWidgetResizeTitle
} from './homeWidgetResize.js'
export {
  homeWidgetMotionAllowed,
  homeWidgetFlipDelta,
  homeWidgetFlipStart,
  homeWidgetFlipCapture,
  homeWidgetFlipPlay,
  homeWidgetFlip,
  homeWidgetExit,
  homeWidgetReveal
} from './homeWidgetMotion.js'
// The durability decision for the home board's hidden map — which entries are
// USER CHOICES (persist) and which are defaults or render clamps (never).
export { persistableHiddenMap } from './homeHiddenPersist.js'
// Whose answer the home board boots from: the SERVER row, or the local cache.
// The row wins whenever it exists — `{}` included, because "hides nothing" is
// an answer and only a MISSING map is an absence.
export { resolveSavedPrefsSource } from './homePrefsBootSource.js'
// THE home widget registry — the house catalog, the widgets a fresh home
// shows, and the admin panel pages' rows (functions/homeWidgetCatalog.js).
export { homeWidgetCatalog, homeDefaultVisibleWidgets, homePanelRows } from './homeWidgetCatalog.js'
// The DEFAULT half of the same map — which widgets a workspace's home board
// shows before anybody personalises it. App-mode workspaces default to the
// installed module's widgets alone; a workspace panel set decides only what
// its writers could say (the house default ids follow the global default).
export {
  isAppModeWorkspace,
  appModeModuleRef,
  takeoverModuleRef,
  houseDefaultHiddenMap,
  panelSetHiddenMap
} from './homeDefaultWidgets.js'
export * from './broadcast.js'
export { sameWorkspaceWriter } from './sameWorkspaceWriter.js'
export * from './aiWidgets.js'
// Assistant starter prompts, per workspace — built from the apps the
// workspace shows, generated by the AI on first use, cached in the member's
// preferences (reached by the shared Chat.js Empty slot via el.call).
export * from './assistantStarters.js'
// The Page view's empty-workspace starter chips: rows for the shared
// AssistantStarterList built from the ai.boards vocabulary, the lazy
// single-flight load, and the chip's board-generating send
// (WS-W2-ASSISTANT-PAGE-MODE-DOCK-1, spec §4 "Empty workspace").
export * from './assistantPageStarters.js'
// The assistant's generated board (DESIGN-WL-ASSISTANT-MAIN-VIEW-1) — the board
// spec → cards, the streaming consumer, pin / save through the SDK, the view's
// helpers and its two keyboard rows (Esc, P). Every export is a function.
export * from './assistantBoard.js'
// A manifest widget's `visibleFn` gate — the ONE evaluation the home board and
// the assistant board share; the gate reaches the shell bridges (G-H).
export * from './moduleWidgetGate.js'
// The assistant's MAIN THREAD (AI_ASSISTANT_SPEC v1 §1 + §8, §12.2): the send
// route (board | deck | thread | today's path), the view's gate and rows,
// show / close, Open in sidebar / Open in main, the page layer's gate, the
// Esc row. Every export is a function.
export * from './assistantMainThread.js'
// THE ASK ROUTER (workspace ai, 2026-10-28): what one ask wants (a page,
// slides, an app, a website, a document, an action, the tour, an answer), the
// view on screen, where the ask goes, the one quiet line that says so and its
// way back, the strong asks that leave the docked drawer for the composer's
// path, and the thread beside an open page. Every export is a function.
export * from './assistantAskRoute.js'
// THE COMPOSER'S CONTEXT + FOCUS PANEL + INSPECT & ASK (DESIGN-COMPOSER-FOCUS-
// POPOVER-CONTEXT-INSPECT-1): the context model (options, the chosen one, the
// one writer, the wire), the panel's grouped rows and three verbs, and the
// inspect mode (start / stop / hover / pick / keys). Every export is a
// function reached through el.call.
export * from './aiScope.js'
export * from './aiPanel.js'
export * from './aiInspect.js'
export * from './aiCapabilities.js'
// Every main-thread conversation has its own URL (`?ai=<conversationId>`):
// the two-way reconcile between the address bar and the thread state.
export * from './assistantThreadUrl.js'
// The assistant's LIVE DECK (Presentation mode, AI_ASSISTANT_SPEC §12.1): the
// DeckSpec → slides derivation, the stream reducer, generate / refine through
// ai.boards with `mode: 'presentation'`, the view's verbs (go, present, save,
// dismiss) and its keyboard rows. Every export is a function.
export * from './assistantDeck.js'
// FLY MODE (docs/FLY_MODE.md): every ask composes a page — the session, the
// fly stream reducer, the records sections filled from the stream, the
// page's verbs (ask again, share, save), the canvas's keyboard row.
export * from './assistantFly.js'
export * from './assistantFlyWorkspace.js'
// Fly's first screen, made from the workspace (its signals and its starters),
// and the home's Dashboard · Fly switch.
export * from './assistantFlyStart.js'
// A page's next asks — and the quick tour among them (a page about what
// Symbols is, how the workspace works or where to start).
export * from './assistantFlyFollow.js'
// A new workspace's first steps on that screen ("Get started"), from the
// server's activation read (fly docs/FUNNEL.md §4 G5) — and its one lead-in
// line, not a step: the Workspace Presets for a workspace that has none.
export * from './assistantFlyFirstRun.js'
// A personalized sign-up's first thread on that screen (POB S7): what research
// read and from where, the three offers and "Getting started" — the server's
// ticks only (components/AssistantFlyResearch.js).
export * from './assistantFlyResearch.js'
export * from './firstRunPresetLine.js'
// Calling anyone in the workspace: the one call (a private room, the person
// its member, the link in a DM) and Fly's two ways in — the start screen's
// People and a "call <name>" ask.
export * from './callPerson.js'
export * from './assistantFlyPeople.js'
// The workspace or its CLI (Agent mode): one switch, Ctrl+`.
export * from './workspaceMode.js'
export * from './incomingCall.js'
export * from './homeView.js'
export * from './homeTabStart.js'
// The Build tab is the app builder — the /add-app page on the home.
export * from './homeBuild.js'
// The builder's sidebars — the person's apps, the conversation's head.
export * from './addAppSidebar.js'
export * from './addAppBuilderChat.js'
// The workspace builds itself (Fly's Make it real, lane WS1): the setup job's
// steps, the plan card and the first build in the first workspace thread
// (components/WorkspaceSetupGate.js, WorkspaceSetupThread.js, WorkspaceSetupPlan.js).
export * from './workspaceSetup.js'
// The ONE workspace rename — the settings name card and the welcome's name
// field (round 2 BLOCKER #5: a direct sign-up names its FIRST workspace).
export * from './workspaceRename.js'
// `visibleOrgs` (which orgs a list OFFERS — POB round 5) is shared's own
// function now (shared/functions/visibleOrgs.js): the org switcher there
// reads it too, and shared's bag reaches every shell context already.
// A new answer's section comes to the top of the view (Fly, the thread, a
// deck, a board).
export * from './assistantReveal.js'
// Agent mode — the workspace TUI as the whole shell (WS-AGENT-MODE-1).
export * from './agentMode.js'
export * from './agentModeRuntime.js'
export * from './agentModeView.js'
export * from './agentModeFocus.js'
export * from './agentModeScroll.js'
export * from './phoneNavigation.js'
export * from './assistantFlyRender.js'
export * from './assistantFlyCall.js'
// A Fly page's READS and its UI sections (Fly train 2): a read's result drawn
// with the chat's own answer-UI renderers, and what a solo ask carries (the
// scope, the parts pointed at, the subjects its cards show).
export * from './assistantFlyUi.js'
export * from './assistantFlyContext.js'
// The RECORDS BLOCK v1 client half (normalize, the ai.boards.records read +
// its per-workspace cache, the four view models, the row opener) and the
// inline blocks' actions (open, save as app / board, refine, the inline view
// mount). Every export is a function.
export * from './assistantRecords.js'
export * from './assistantThreadBlocks.js'
// APP VIEWS and APP FORMS in an AI answer: an app's read-only views
// (manifest views[] / the built-in providers) and its own forms, prefilled
// from a proposal (manifest forms[]) — the vocabulary, access, the lazy fold,
// the links, the form's open and the app's saved report. Every export is a
// function.
export * from './assistantAppViews.js'
export * from './assistantAppForms.js'
// THE SITE BUILDER's entry for a website ask (the ask router's 'website'
// intent): a normal send with a site on screen, else the builder opens first.
export * from './assistantOpenSiteBuilder.js'
// The built-in comms views' list frame (read once per params, the model,
// the row and group shapes) and the shell's own views: the team and its
// agents, the notifications, the reminders.
export * from './assistantViewList.js'
export * from './workspaceAnswer.js'
// WEB SOURCES in an AI answer (U4): source cards and a fetched page's quote.
export * from './assistantSources.js'
// The home Brief's AI source (ai.boards.summary, 800 ms, template fallback).
export * from './homeSummaryAi.js'
// The minimized placement's answer popover: which answer, the two moves, the
// placement above the pill, the Esc row.
export * from './assistantAnswerPopover.js'
// Home widget numerals — compact by contract (HOME_WIDGETS_SPEC.md §2 Hero):
// the module bodies' heroes and row metas reach them through el.call.
export { homeCompactNumber, homeMoney } from './homeNumbers.js'
// The shell keyboard: THE registry + guard (shortcuts.js), the default map
// (shortcutsDefault.js), the home-board half (shortcutsHome.js) — wired by ONE
// keydown listener, the shell root's declared onDocumentKeydown (app.js) — and
// the page/route motion (routeTransition.js). DESIGN-WL-SHELL-KEYBOARD-MOTION-1.
export * from './shortcuts.js'
// mailto: — the in-app composer as the DEFAULT action of a plain mailto
// click (the shell root's onDocumentClick, app.js).
export * from './mailtoComposeDefault.js'
export * from './shortcutsDefault.js'
export * from './shortcutsHome.js'
export * from './routeTransition.js'
// `?board=<id>` — the board on screen in the home's address (ws-home's half
// of the boards row, DESIGN-WL-ASSISTANT-MAIN-HOME-1).
export {
  homeBoardUrlRead,
  homeBoardUrlSync,
  homeBoardUrlOpen,
  homeBoardUrlTick
} from './homeBoardUrl.js'
// The home "Next up" widget's reads (HOME_AMBIENT_SKIN_SPEC.md §4).
export {
  homeNextUpEvents,
  homeNextUpHero,
  homeNextUpTime,
  homeNextUpRows,
  homeNextUpAction,
  homeNextUpActionArg,
  homeNextUpOpen,
  homeNextUpNormalize,
  homeNextUpProviders,
  homeNextUpCollect,
  homeNextUpItems,
  homeNextUpAppsKnown,
  homeNextUpProviderSig,
  homeNextUpClock,
  homeNextUpClockStop
} from './homeNextUp.js'
export { homePinnedBlock, homePinnedHostRow } from './homePinnedItems.js'
// The home top (DESIGN-WL-HOME-TOP-STRIP-SUMMARY-1): the person's own look of
// it (App strip on/off, Dock | Rail, names, Brief | Signals, the sky — one
// user_preferences bag), the signals collector of HOME_SIGNALS_CONTRACT v1
// with the built-in providers (tasks, calendar, mail) and the clock, and the
// ONE summary source the sub-line and both summary styles render from.
export * from './homeLook.js'
export * from './homeSignals.js'
export * from './homeSummaryCompose.js'
export * from './autoTopup.js'
// /admin/usage: the return leg of a credit top-up checkout (confirm + line).
export * from './topupReturn.js'
// The user-code frame runtime text (see the file header).
export { userCodeRuntimeText } from './userCodeRuntimeText.js'
export * from './inviteDropdownMode.js'
// The skip link's target: the open window, else the page's main landmark.
export * from './skipToMainHref.js'
// The streams and session reads a page with no session never opens (persona
// audit OV-2): the shell's meet channel and the persona oracle.
export * from './sessionStreams.js'
// The home's context-menu tip — once, never over content — and the keyboard
// way into the same menus (persona audit WH-9).
export * from './homeCtxTip.js'
// The admin rail's entries in their groups, and the business-workspace rule
// that folds the developer group (persona audit WH-8).
export * from './adminRail.js'
// The avatar menu's Language row — the shell builds it and applies a pick
// (persona audit WL-2; shared/functions/accountMenu.js shows it).
export * from './workspaceLanguageMenu.js'
// The shell root's gate in front of the workspace language policy: a
// signed-out shell keeps the public language (QA round 4, giorgi /login).
export * from './shellLangPolicy.js'
